import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  CalendarDaysIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  XMarkIcon,
  CheckCircleIcon,
  HomeIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  EyeIcon,
  DocumentCheckIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  CheckIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import {
  fetchRenewals,
  fetchAttentionUnits,
  sendRenewalEmail,
  updateRenewal,
  RenewalApiEntry,
  RenewalApiOccupant,
  RenewalAttentionApiEntry,
} from "./DataServices/RenewalDataService";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type RenewalStage =
  | "up_for_renewal" // 1  – set price
  | "sent_out" // 2  – renewal sent, awaiting response
  | "renewed" // 3a – Green
  | "partially_signed" // 3b – Yellow
  | "not_renewing" // 3c – Bright Blue
  | "being_marketed" // 4  – Orange
  | "rented"; // 5  – Dark Blue

interface ShowingStats {
  futureShowings: number;
  pastShowings: number;
  applicationsIn: number;
  completeApplications: number;
  completeWithPayment: number;
}

interface RentalRollEntry {
  id: string;
  publicApartmentUnitId?: string;
  unit: string;
  building: string;
  buildingAddress: string;
  tenant: string;
  email: string;
  phone: string;
  rent: number;
  newRent: number;
  deposit: number;
  leaseFrom: string;
  leaseTo: string;
  lateCount: number;
  stage: RenewalStage;
  sourceStatus?: string;
  sourceStage?: RenewalStage;
  sourceMappedStatus?: string;
  dashboardStatus?: string;
  hasStatusMismatch?: boolean;
  notes: string;
  vacateDate?: string;
  marketingStartDate?: string;
  showingStats?: ShowingStats;
  unitLine?: string; // Internal Unit Line (e.g. "A", "16")
  actualLine?: string;
  comparableGroup?: string; // Comparable Group (e.g. "16 & 17 Lines Group")
  occupants?: RenewalApiOccupant[];
}

type ZoomLevel = 0 | 1 | 2 | 3 | 4;

export interface GroupMember {
  name: string;
  email: string;
  phone: string;
  isGuarantor: boolean;
}

export function getGroupMembers(e: RentalRollEntry): GroupMember[] {
  if (e.occupants && e.occupants.length > 0) {
    return e.occupants.map((occupant) => ({
      name: occupant.name,
      email: occupant.email || "",
      phone: occupant.phone || "",
      isGuarantor: occupant.isCosigner,
    }));
  }
  return [];
}

export function getIndividualEmailDetails(e: RentalRollEntry, m: GroupMember) {
  const subject = `Lease Renewal Option - Unit ${e.unit} (${e.building})`;
  const body = `Hi ${m.name},

I hope you are doing well. We are reaching out regarding your lease renewal option for Unit ${e.unit} at ${e.buildingAddress}.

Your current lease is scheduled to expire on ${e.leaseTo}. The renewal rate is proposed at $${e.newRent.toLocaleString()}/month.

Please let us know if you would like to renew or have any questions.

Best regards,
Management`;
  return { subject, body };
}

export function getGroupEmailDetails(
  e: RentalRollEntry,
  members: GroupMember[],
) {
  const names = members.map((x) => x.name).join(", ");
  const subject = `Lease Renewal Option - Unit ${e.unit} (${e.building})`;
  const body = `Hi ${names},

I hope you are doing well. We are reaching out regarding your lease renewal option for Unit ${e.unit} at ${e.buildingAddress}.

Your current lease is scheduled to expire on ${e.leaseTo}. The renewal rate is proposed at $${e.newRent.toLocaleString()}/month.

Please let us know if you would like to renew or have any questions.

Best regards,
Management`;
  return { subject, body };
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const TODAY = (() => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
})();
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const ZOOM_LABELS: Record<ZoomLevel, string> = {
  0: "Calendar",
  1: "Month List",
  2: "Quarter",
  3: "Attention",
  4: "Not Renewed / Marketing",
};

// ─── Stage Config (color coding per user spec) ─────────────────────────────────

const STAGE_CFG: Record<
  RenewalStage,
  {
    step: string;
    label: string;
    sublabel: string;
    text: string;
    bg: string;
    rowBg: string;
    border: string;
    borderL: string;
    header: string;
    dot: string;
    color: string;
  }
> = {
  up_for_renewal: {
    step: "1",
    label: "Up for Renewal",
    sublabel: "Set Price",
    text: "text-[#7c5d41]",
    bg: "bg-[#f6f1eb]",
    rowBg: "bg-[#f6f1eb]/80",
    border: "border-[#e4dacd]",
    borderL: "border-l-[#a78b71]",
    header: "bg-[#8c6f55]",
    dot: "bg-[#a78b71]",
    color: "#a78b71",
  },
  sent_out: {
    step: "2",
    label: "Renewal Sent Out",
    sublabel: "Awaiting Response",
    text: "text-blue-700",
    bg: "bg-blue-100/60",
    rowBg: "bg-blue-100/60",
    border: "border-blue-200/80",
    borderL: "border-l-blue-400",
    header: "bg-blue-600",
    dot: "bg-blue-500",
    color: "#3b82f6",
  },
  renewed: {
    step: "3a",
    label: "Renewed",
    sublabel: "Confirmed — Green",
    text: "text-emerald-700",
    bg: "bg-emerald-100/60",
    rowBg: "bg-emerald-100/50",
    border: "border-emerald-200/80",
    borderL: "border-l-emerald-400",
    header: "bg-emerald-600",
    dot: "bg-emerald-500",
    color: "#10b981",
  },
  partially_signed: {
    step: "3b",
    label: "Partially Signed",
    sublabel: "Partial Sigs · Unsure — Yellow",
    text: "text-amber-700",
    bg: "bg-amber-100/60",
    rowBg: "bg-amber-100/70",
    border: "border-amber-200/80",
    borderL: "border-l-amber-400",
    header: "bg-amber-500",
    dot: "bg-amber-500",
    color: "#f59e0b",
  },
  not_renewing: {
    step: "3c",
    label: "Not Renewing",
    sublabel: "Moving Out — Bright Blue",
    text: "text-sky-700",
    bg: "bg-sky-100/60",
    rowBg: "bg-sky-100/60",
    border: "border-sky-200/80",
    borderL: "border-l-sky-400",
    header: "bg-sky-500",
    dot: "bg-sky-500",
    color: "#0ea5e9",
  },
  being_marketed: {
    step: "4",
    label: "Being Marketed",
    sublabel: "Showings Active",
    text: "text-purple-700",
    bg: "bg-purple-100/60",
    rowBg: "bg-purple-100/60",
    border: "border-purple-200/80",
    borderL: "border-l-purple-400",
    header: "bg-purple-600",
    dot: "bg-purple-500",
    color: "#a855f7",
  },
  rented: {
    step: "5",
    label: "Rented",
    sublabel: "New Tenant — Dark Blue",
    text: "text-indigo-700",
    bg: "bg-indigo-100/60",
    rowBg: "bg-indigo-100/60",
    border: "border-indigo-200/80",
    borderL: "border-l-indigo-400",
    header: "bg-indigo-600",
    dot: "bg-indigo-500",
    color: "#6366f1",
  },
};

const STAGE_ORDER: RenewalStage[] = [
  "up_for_renewal",
  "sent_out",
  "renewed",
  "partially_signed",
  "not_renewing",
  "being_marketed",
  "rented",
];

// ─── Helpers ────────────────────────────────────────────────────────────────────

function parseDate(str: string | null | undefined): Date | null {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function safeStage(value?: string): RenewalStage {
  const normalized = (value || "").trim().toLowerCase();
  if (STAGE_ORDER.includes(normalized as RenewalStage)) {
    return normalized as RenewalStage;
  }

  switch (normalized.replace(/[\s-]+/g, "_")) {
    case "up_for_renewal":
    case "upforrenewal":
      return "up_for_renewal";
    case "renewal_sent_out":
    case "renewalsentout":
    case "sent_out":
    case "sentout":
      return "sent_out";
    case "renewed":
      return "renewed";
    case "partially_signed":
    case "partiallysigned":
      return "partially_signed";
    case "not_renewing":
    case "notrenewing":
      return "not_renewing";
    case "being_marketed":
    case "beingmarketed":
      return "being_marketed";
    case "rented":
      return "rented";
    default:
      return "up_for_renewal";
  }
}

function stageToApiStatus(stage: RenewalStage): string {
  switch (stage) {
    case "up_for_renewal":
      return "UpForRenewal";
    case "sent_out":
      return "RenewalSentOut";
    case "renewed":
      return "Renewed";
    case "partially_signed":
      return "PartiallySigned";
    case "not_renewing":
      return "NotRenewing";
    case "being_marketed":
      return "BeingMarketed";
    case "rented":
      return "Rented";
    default:
      return "UpForRenewal";
  }
}

function mapApiRenewal(row: RenewalApiEntry): RentalRollEntry {
  const rent = Number(row.rent ?? 0);
  const newRent = Number(row.proposedRent ?? 0);

  return {
    id: row.id,
    publicApartmentUnitId: row.publicApartmentUnitId,
    unit: row.unit || row.apartmentTitle || "Unknown",
    building: row.building || "Unknown Building",
    buildingAddress: row.buildingAddress || "",
    tenant: row.tenant || "",
    email: row.email || "",
    phone: row.phone || "",
    rent,
    newRent,
    deposit: Number(row.deposit ?? 0),
    leaseFrom: row.leaseFrom || "",
    leaseTo: row.leaseTo || "",
    lateCount: Number(row.lateCount ?? 0),
    stage: safeStage(row.dashboardStage),
    sourceStatus: row.sourceStatus,
    sourceStage: safeStage(row.sourceStage),
    sourceMappedStatus: row.sourceMappedStatus,
    dashboardStatus: row.dashboardStatus,
    hasStatusMismatch: row.hasStatusMismatch,
    notes: row.notes || "",
    unitLine: row.renewalLine || "",
    actualLine: row.actualLine,
    comparableGroup: row.comparableGroup || "",
    occupants: row.occupants || [],
  };
}

function daysBetween(a: Date, b: Date) {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}
function tenancyYears(leaseFrom: string) {
  const d = parseDate(leaseFrom);
  if (!d) return 0;
  return (TODAY.getTime() - d.getTime()) / (86400000 * 365);
}
function outreachWindow(leaseFrom: string) {
  return tenancyYears(leaseFrom) >= 2 ? 95 : 65;
}
function getDaysUntil(dateStr: string) {
  const d = parseDate(dateStr);
  if (!d) return 0;
  return daysBetween(TODAY, d);
}
function getOutreachTargetDate(
  leaseTo: string,
  leaseFrom: string,
): Date | null {
  const d = parseDate(leaseTo);
  if (!d) return null;
  const lead = outreachWindow(leaseFrom);
  d.setDate(d.getDate() - lead);
  return d;
}
function getDaysUntilOutreach(leaseTo: string, leaseFrom: string): number {
  const target = getOutreachTargetDate(leaseTo, leaseFrom);
  if (!target) return 0;
  return daysBetween(TODAY, target);
}

// ─── Shared Badges ─────────────────────────────────────────────────────────────

function StageBadge({ stage }: { stage: RenewalStage }) {
  const c = STAGE_CFG[stage];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

interface CustomDropdownProps {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder?: string;
  customActionLabel: string;
  onCustomAction: () => void;
  suffix?: string;
}

function CustomDropdown({
  value,
  onChange,
  options,
  placeholder = "Unassigned",
  customActionLabel,
  onCustomAction,
  suffix = "",
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedDisplay = value ? `${value}${suffix}` : placeholder;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-slate-205 border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 font-semibold text-slate-800 text-left transition-all"
      >
        <span
          className={value ? "text-slate-800" : "text-slate-400 font-semibold"}
        >
          {selectedDisplay}
        </span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-250 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden animate-scale-up">
          <div className="max-h-[160px] overflow-y-auto divide-y divide-slate-100/50 scrollbar-thin">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-xs font-semibold hover:bg-slate-50 transition-colors ${
                !value ? "text-blue-600 bg-blue-50/40" : "text-slate-400"
              }`}
            >
              {placeholder}
            </button>

            {options.map((opt) => {
              const active = value === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs font-semibold hover:bg-slate-50 transition-colors ${
                    active
                      ? "text-blue-600 bg-blue-50/40 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  {opt}
                  {suffix}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => {
              onCustomAction();
              setIsOpen(false);
            }}
            className="w-full text-left px-3 py-2.5 text-xs font-bold text-blue-600 bg-slate-50 hover:bg-blue-50 hover:text-blue-700 border-t border-slate-100 transition-colors"
          >
            {customActionLabel}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Filter Dropdown Component ──────────────────────────────────────────────────

interface FilterDropdownProps {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  placeholder: string;
  displayMapping?: (val: string) => string;
}

function FilterDropdown({
  value,
  onChange,
  options,
  placeholder,
  displayMapping,
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedDisplay =
    value && value !== "all"
      ? displayMapping
        ? displayMapping(value)
        : value
      : placeholder;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-1.5 border border-slate-205 border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 font-semibold text-slate-800 text-left transition-all hover:border-slate-300 shadow-2xs"
      >
        <span
          className={
            value && value !== "all"
              ? "text-slate-800 font-semibold"
              : "text-slate-400 font-semibold"
          }
        >
          {selectedDisplay}
        </span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden animate-scale-up">
          <div className="max-h-[160px] overflow-y-auto divide-y divide-slate-100/50 scrollbar-thin">
            <button
              type="button"
              onClick={() => {
                onChange("all");
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-xs font-semibold hover:bg-slate-50 transition-colors ${
                value === "all" || !value
                  ? "text-blue-600 bg-blue-50/40 font-bold"
                  : "text-slate-450"
              }`}
            >
              {placeholder}
            </button>

            {options.map((opt) => {
              const active = value === opt;
              const displayOpt = displayMapping ? displayMapping(opt) : opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs font-semibold hover:bg-slate-50 transition-colors ${
                    active
                      ? "text-blue-600 bg-blue-50/40 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  {displayOpt}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Entry Detail Modal ─────────────────────────────────────────────────────────

function EntryModal({
  entry,
  onClose,
  onStageChange,
  allEntries,
  onComposeEmail,
  customCreatedLines = {},
  customCreatedGroups = {},
  onAddCustomLine,
  onAddCustomGroup,
}: {
  entry: RentalRollEntry;
  onClose: () => void;
  onStageChange: (
    id: string,
    stage: RenewalStage,
    notes: string,
    newRent: number,
    unitLine: string,
    compGroup: string,
    currentRent?: number,
  ) => void;
  allEntries: RentalRollEntry[];
  onComposeEmail: (
    recipients: string,
    names: string,
    subject: string,
    body: string,
    renewalId?: string,
  ) => void;
  customCreatedLines?: Record<string, string[]>;
  customCreatedGroups?: Record<string, string[]>;
  onAddCustomLine?: (building: string, line: string) => void;
  onAddCustomGroup?: (building: string, group: string) => void;
}) {
  const [stage, setStage] = useState<RenewalStage>(entry.stage);
  const [notes, setNotes] = useState(entry.notes);
  const [newRent, setNewRent] = useState(entry.newRent);
  const [rent, setRent] = useState(entry.rent);
  const [unitLine, setUnitLine] = useState(entry.unitLine || "");
  const [comparableGroup, setComparableGroup] = useState(
    entry.comparableGroup || "",
  );

  const handleClose = () => {
    const lineChanged = unitLine !== (entry.unitLine || "");
    const groupChanged = comparableGroup !== (entry.comparableGroup || "");

    if (lineChanged || groupChanged) {
      onStageChange(
        entry.id,
        entry.stage,
        entry.notes,
        entry.newRent,
        unitLine,
        comparableGroup,
        entry.rent,
      );
    }
    onClose();
  };

  const handleSaveAndClose = () => {
    const originalRent = entry.newRent;
    const hasChanges =
      stage !== entry.stage ||
      notes !== entry.notes ||
      newRent !== originalRent ||
      rent !== entry.rent ||
      unitLine !== (entry.unitLine || "") ||
      comparableGroup !== (entry.comparableGroup || "");

    if (hasChanges) {
      onStageChange(
        entry.id,
        stage,
        notes,
        newRent,
        unitLine,
        comparableGroup,
        rent,
      );
    }
    onClose();
  };
  // Filter existing unit lines
  const existingLines = useMemo(() => {
    const lines = new Set<string>();
    const seen = new Set<string>();
    allEntries.forEach((e) => {
      if (e.unitLine) {
        const val = e.unitLine.trim();
        const lower = val.toLowerCase();
        if (!seen.has(lower)) {
          seen.add(lower);
          lines.add(val);
        }
      }
    });

    Object.values(customCreatedLines).forEach((list) => {
      list.forEach((line) => {
        const val = line.trim();
        const lower = val.toLowerCase();
        if (!seen.has(lower)) {
          seen.add(lower);
          lines.add(val);
        }
      });
    });
    return Array.from(lines).sort();
  }, [allEntries, customCreatedLines]);

  // Filter existing comparable groups in the current building
  const existingGroups = useMemo(() => {
    const groups = new Set<string>();
    const seen = new Set<string>();
    allEntries.forEach((e) => {
      if (e.building === entry.building && e.comparableGroup) {
        const val = e.comparableGroup.trim();
        const lower = val.toLowerCase();
        if (!seen.has(lower)) {
          seen.add(lower);
          groups.add(val);
        }
      }
    });
    const customList = customCreatedGroups[entry.building] || [];
    customList.forEach((group) => {
      const val = group.trim();
      const lower = val.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        groups.add(val);
      }
    });
    return Array.from(groups).sort();
  }, [allEntries, entry.building, customCreatedGroups]);

  const [showCreateLineModal, setShowCreateLineModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [customLineInput, setCustomLineInput] = useState("");
  const [customGroupInput, setCustomGroupInput] = useState("");

  const [leaseOpen, setLeaseOpen] = useState(true);
  const [membersOpen, setMembersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [showAllComparables, setShowAllComparables] = useState(false);

  const dropdownLines = useMemo(() => {
    const list = [...existingLines];
    if (
      unitLine &&
      !existingLines
        .map((x) => x.toLowerCase())
        .includes(unitLine.toLowerCase())
    ) {
      list.push(unitLine);
    }
    return list.sort();
  }, [existingLines, unitLine]);

  const dropdownGroups = useMemo(() => {
    const list = [...existingGroups];
    if (
      comparableGroup &&
      !existingGroups
        .map((x) => x.toLowerCase())
        .includes(comparableGroup.toLowerCase())
    ) {
      list.push(comparableGroup);
    }
    return list.sort();
  }, [existingGroups, comparableGroup]);

  const leaseEnd = parseDate(entry.leaseTo);
  const daysLeft = getDaysUntil(entry.leaseTo);

  // Filter comparable units in the same building by matching group or unit line
  const comparables = useMemo(() => {
    return allEntries.filter((e) => {
      if (e.id === entry.id || e.building !== entry.building) return false;
      const matchGroup =
        comparableGroup &&
        e.comparableGroup?.toLowerCase() === comparableGroup.toLowerCase();
      const matchLine =
        unitLine && e.unitLine?.toLowerCase() === unitLine.toLowerCase();
      return matchGroup || matchLine;
    });
  }, [allEntries, entry, comparableGroup, unitLine]);

  const compAvg = useMemo(() => {
    const activeComps = comparables.filter((c) => c.newRent > 0 || c.rent > 0);
    if (activeComps.length === 0) return 0;
    const total = activeComps.reduce(
      (sum, c) => sum + (c.newRent > 0 ? c.newRent : c.rent),
      0,
    );
    return Math.round(total / activeComps.length);
  }, [comparables]);

  const rentChange = newRent - rent;
  const rentChangePct =
    rent > 0 ? ((rentChange / rent) * 100).toFixed(1) : "0.0";
  const addtlDeposit = newRent - entry.deposit;
  const visibleComparables = showAllComparables
    ? comparables
    : comparables.slice(0, 3);
  const stageCfg = STAGE_CFG[stage];
  const members = getGroupMembers(entry);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end backdrop-blur-[2px] transition-all"
      style={{ background: "rgba(15,23,42,0.3)" }}
      onClick={handleClose}
    >
      {/* ── Sidebar Panel ── */}
      <div
        className="relative w-full sm:w-[440px] bg-white h-full flex flex-col shadow-2xl border-l border-slate-200 animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── FIXED HEADER ── */}
        <div className="flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-200 px-5 pt-4 pb-2.5 sticky top-0 z-30">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-black text-slate-900 leading-tight truncate">
                {entry.building ? `${entry.building} - ` : ""}
                {entry.unit}
                {entry.unitLine ? ` (${entry.unitLine} Line)` : ""}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${stageCfg.bg} ${stageCfg.text} ${stageCfg.border}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${stageCfg.dot}`} />
              {stageCfg.label}
            </span>
            {entry.sourceStatus && (
              <span className="text-xs text-slate-400 font-bold">
                ({entry.sourceStatus})
              </span>
            )}
            {entry.hasStatusMismatch && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border bg-amber-50 text-amber-700 border-amber-200">
                ⚠ Mismatch
              </span>
            )}
          </div>

          {entry.leaseTo && leaseEnd && (
            <div className="space-y-1.5">
              <div className="text-[11px] text-slate-400 font-medium">
                Lease Expires:{" "}
                <span className="text-slate-650 font-bold">
                  {leaseEnd.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "2-digit",
                  })}
                </span>
              </div>
            </div>
          )}

          {/* Quick suggestions banner */}
          {stage === "not_renewing" && (
            <div className="mt-3 bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between gap-2 shadow-2xs">
              <div className="min-w-0">
                <div className="text-[9px] font-black text-purple-700 uppercase tracking-wide">
                  Suggested Next Action
                </div>
                <div className="text-[11px] text-purple-650 font-bold truncate mt-0.5">
                  Move to Marketing pipeline
                </div>
              </div>
              <button
                onClick={() => {
                  onStageChange(
                    entry.id,
                    "being_marketed",
                    notes,
                    newRent,
                    unitLine,
                    comparableGroup,
                    rent,
                  );
                  onClose();
                }}
                className="px-2.5 py-1 text-[10px] font-black bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all flex items-center gap-1 shadow-sm shrink-0"
              >
                Start Marketing
              </button>
            </div>
          )}
          {stage === "being_marketed" && (
            <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center justify-between gap-2 shadow-2xs">
              <div className="min-w-0">
                <div className="text-[9px] font-black text-indigo-700 uppercase tracking-wide">
                  Suggested Next Action
                </div>
                <div className="text-[11px] text-indigo-650 font-bold truncate mt-0.5">
                  Move unit to Rented status
                </div>
              </div>
              <button
                onClick={() => {
                  onStageChange(
                    entry.id,
                    "rented",
                    notes,
                    newRent,
                    unitLine,
                    comparableGroup,
                    rent,
                  );
                  onClose();
                }}
                className="px-2.5 py-1 text-[10px] font-black bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all flex items-center gap-1 shadow-sm shrink-0"
              >
                Mark as Rented
              </button>
            </div>
          )}
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-5 pt-2.5 pb-5 space-y-3.5 bg-slate-50/40">
          {/* Financials Section */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Rent Input */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider block">
                  Rent
                </label>
                <div className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/30 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all">
                  <span className="text-sm font-black text-slate-400">$</span>
                  <input
                    type="number"
                    value={rent}
                    onChange={(e) => setRent(Number(e.target.value))}
                    className="w-full text-base font-black text-slate-700 bg-transparent focus:outline-none p-0 border-0 focus:ring-0"
                  />
                </div>
              </div>

              {/* Proposed Rent Input */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider block">
                    Proposed Rent
                  </label>
                  {compAvg > 0 && newRent !== compAvg && (
                    <button
                      type="button"
                      onClick={() => setNewRent(compAvg)}
                      className="text-[8.5px] font-black text-blue-600 hover:text-blue-800 transition-colors"
                      title={`Comparable Average: $${compAvg}`}
                    >
                      Use Avg
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/30 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all">
                  <span className="text-sm font-black text-slate-400">$</span>
                  <input
                    type="number"
                    value={newRent}
                    onChange={(e) => setNewRent(Number(e.target.value))}
                    className="w-full text-base font-black text-slate-800 bg-transparent focus:outline-none p-0 border-0 focus:ring-0"
                  />
                </div>
              </div>
            </div>

            {/* Summary Cards Row */}
            <div className="grid grid-cols-2 gap-3 pt-3.5 border-t border-slate-100">
              {/* Rent Change Card */}
              <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50/40 rounded-xl border border-slate-200/60">
                <div>
                  <div className="text-[8px] font-extrabold text-slate-400 uppercase tracking-wider">
                    Rent Change
                  </div>
                  <div className="text-xs font-black mt-0.5">
                    {rentChange === 0 ? (
                      <span className="text-slate-450">—</span>
                    ) : rentChange > 0 ? (
                      <span className="text-green-600">
                        +${rentChange.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-red-500">
                        -${Math.abs(rentChange).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                {rentChange !== 0 && (
                  <span
                    className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${rentChange > 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-650"}`}
                  >
                    {rentChange > 0 ? "+" : ""}
                    {rentChangePct}%
                  </span>
                )}
              </div>

              {/* Late Payments Card */}
              <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50/40 rounded-xl border border-slate-200/60">
                <div>
                  <div className="text-[8px] font-extrabold text-slate-400 uppercase tracking-wider">
                    Late Payments
                  </div>
                  <div
                    className={`text-xs font-black mt-0.5 ${entry.lateCount > 0 ? "text-red-600" : "text-slate-700"}`}
                  >
                    {entry.lateCount}
                  </div>
                </div>
                <span
                  className={`p-1 rounded-lg ${entry.lateCount > 0 ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-400"}`}
                >
                  <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                </span>
              </div>

              {/* Current Deposit Card */}
              <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50/40 rounded-xl border border-slate-200/60">
                <div>
                  <div className="text-[8px] font-extrabold text-slate-400 uppercase tracking-wider">
                    Current Deposit
                  </div>
                  <div className="text-xs font-black mt-0.5 text-slate-700">
                    ${entry.deposit.toLocaleString()}
                  </div>
                </div>
                <span className="p-1 rounded-lg bg-slate-100 text-slate-400">
                  <ShieldCheckIcon className="w-3.5 h-3.5" />
                </span>
              </div>

              {/* Additional Deposit Card */}
              <div className="flex items-center justify-between px-3 py-2.5 bg-slate-50/40 rounded-xl border border-slate-200/60">
                <div>
                  <div className="text-[8px] font-extrabold text-slate-400 uppercase tracking-wider">
                    Addtl Deposit
                  </div>
                  <div
                    className={`text-xs font-black mt-0.5 ${addtlDeposit > 0 ? "text-green-600" : "text-slate-500"}`}
                  >
                    ${addtlDeposit.toLocaleString()}
                  </div>
                </div>
                <span
                  className={`p-1 rounded-lg ${addtlDeposit > 0 ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-400"}`}
                >
                  <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </div>

          {/* Lease Schedule Accordion */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setLeaseOpen(!leaseOpen)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <CalendarDaysIcon className="w-4 h-4 text-slate-500" />
                Lease Schedule
              </div>
              <ChevronDownIcon
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${leaseOpen ? "rotate-180" : ""}`}
              />
            </button>
            {leaseOpen && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-3 pt-3">
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Commenced
                    </div>
                    <div className="text-xs font-bold text-slate-800">
                      {parseDate(entry.leaseFrom)
                        ? parseDate(entry.leaseFrom)!.toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Expiration
                    </div>
                    <div className="text-xs font-bold text-slate-800">
                      {entry.leaseTo && leaseEnd
                        ? leaseEnd.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                    </div>
                  </div>
                </div>
                {entry.stage !== "renewed" &&
                  entry.leaseTo &&
                  leaseEnd &&
                  parseDate(entry.leaseFrom) && (
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center text-[10px] font-semibold">
                      <span className="text-slate-400">Lease Progression</span>
                      <span
                        className={
                          daysLeft < 0
                            ? "text-red-600"
                            : daysLeft <= 90
                              ? "text-amber-600"
                              : "text-slate-500"
                        }
                      >
                        {daysLeft < 0
                          ? `Expired ${Math.abs(daysLeft)}d ago`
                          : `${daysLeft} days remaining`}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${daysLeft < 0 ? "bg-red-500" : daysLeft <= 90 ? "bg-amber-500" : "bg-blue-500"}`}
                        style={{
                          width: `${Math.max(0, Math.min(100, ((Date.now() - parseDate(entry.leaseFrom)!.getTime()) / (leaseEnd.getTime() - parseDate(entry.leaseFrom)!.getTime())) * 100))}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                {entry.vacateDate && parseDate(entry.vacateDate) && (
                  <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 flex justify-between items-center text-xs font-bold text-sky-800">
                    <span>Vacate Date:</span>
                    <span>
                      {parseDate(entry.vacateDate)!.toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Comparable & Line Settings Accordion */}
          <div
            className={`bg-white rounded-xl border border-slate-200 shadow-sm ${settingsOpen ? "" : "overflow-hidden"}`}
          >
            <button
              type="button"
              onClick={() => setSettingsOpen(!settingsOpen)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <BuildingOfficeIcon className="w-4 h-4 text-slate-500" />
                Comparable &amp; Line Settings
              </div>
              <ChevronDownIcon
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${settingsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {settingsOpen && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                <div>
                  <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Unit Line (Internal)
                  </label>
                  <CustomDropdown
                    value={unitLine}
                    onChange={setUnitLine}
                    options={dropdownLines}
                    placeholder="Unassigned"
                    customActionLabel="+ Enter Custom Line..."
                    onCustomAction={() => {
                      setCustomLineInput("");
                      setShowCreateLineModal(true);
                    }}
                    suffix=""
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Comparable Group
                  </label>
                  <CustomDropdown
                    value={comparableGroup}
                    onChange={setComparableGroup}
                    options={dropdownGroups}
                    placeholder="Unassigned"
                    customActionLabel="+ Create New Group..."
                    onCustomAction={() => {
                      setCustomGroupInput("");
                      setShowCreateGroupModal(true);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Workflow Pipeline Accordion */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setWorkflowOpen(!workflowOpen)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <ArrowPathIcon className="w-4 h-4 text-slate-500" />
                Workflow Pipeline
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full border ${stageCfg.bg} ${stageCfg.text} ${stageCfg.border}`}
                >
                  {stageCfg.label}
                </span>
                <ChevronDownIcon
                  className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${workflowOpen ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            {workflowOpen && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                <div className="flex flex-wrap gap-1.5">
                  {(
                    [
                      "up_for_renewal",
                      "sent_out",
                      "renewed",
                      "partially_signed",
                      "not_renewing",
                      "being_marketed",
                      "rented",
                    ] as RenewalStage[]
                  ).map((s) => {
                    const c = STAGE_CFG[s];
                    const active = stage === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStage(s)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-bold transition-all duration-150 ${
                          active
                            ? `${c.bg} ${c.text} ${c.border} ring-1 ring-offset-1 ring-blue-400 shadow-sm`
                            : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${c.dot} ${active ? "animate-pulse" : ""}`}
                        />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${stageCfg.dot} mt-0.5 flex-shrink-0`}
                  />
                  <span>
                    {stage === "up_for_renewal" &&
                      "Set offer price, adjust lease terms, and review financial feasibility before sending proposal."}
                    {stage === "sent_out" &&
                      "Offer proposal sent to the lease group; awaiting reviews or signatures from all tenants."}
                    {stage === "renewed" &&
                      "Lease successfully signed and executed by all parties. Schedules updated."}
                    {stage === "partially_signed" &&
                      "Lease is partially signed; awaiting remaining signatures or final negotiations."}
                    {stage === "not_renewing" &&
                      "Tenant declined renewal. Prepare transition, move-out inspection, and deposit return."}
                    {stage === "being_marketed" &&
                      "Unit listed on marketing channels. Pre-leasing active, showings scheduled, and applications under review."}
                    {stage === "rented" &&
                      "New tenant signed, deposit paid, and lease finalized. Move-in scheduled."}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Notes & Activity Accordion */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setNotesOpen(!notesOpen)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <ChatBubbleLeftRightIcon className="w-4 h-4 text-slate-500" />
                Notes &amp; Activity
              </div>
              <div className="flex items-center gap-2">
                {notes && notes.length > 0 && (
                  <span className="text-[9px] font-extrabold bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded-full">
                    1
                  </span>
                )}
                <ChevronDownIcon
                  className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${notesOpen ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            {notesOpen && (
              <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Enter comments, negotiation updates, etc..."
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none font-medium text-slate-700 placeholder-slate-300 transition-all"
                />
              </div>
            )}
          </div>

          {/* Lease Group Members Accordion */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              type="button"
              onClick={() => {
                if (members.length > 0) {
                  setMembersOpen(!membersOpen);
                }
              }}
              className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                members.length > 0
                  ? "hover:bg-slate-50 cursor-pointer"
                  : "cursor-default"
              }`}
            >
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <UserGroupIcon className="w-4 h-4 text-slate-500" />
                Lease Group Members
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-extrabold bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full">
                  {members.length}
                </span>
                {members.length > 0 && (
                  <ChevronDownIcon
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${membersOpen ? "rotate-180" : ""}`}
                  />
                )}
              </div>
            </button>
            {membersOpen && members.length > 0 && (
              <div className="border-t border-slate-100">
                <div className="divide-y divide-slate-100 max-h-52 overflow-y-auto scrollbar-thin">
                  {members.map((m, idx) => {
                    const roleBracket = m.isGuarantor
                      ? "(Guarantor)"
                      : "(Tenant)";
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-slate-800 truncate">
                              {m.name}
                            </span>
                            <span className="text-[9px] text-slate-400 font-extrabold flex-shrink-0">
                              {roleBracket}
                            </span>
                          </div>
                          {m.email ? (
                            <div className="text-[10px] text-slate-400 font-medium truncate mt-0.5">
                              {m.email}
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-300 italic mt-0.5">
                              No email on file
                            </div>
                          )}
                          {m.phone && (
                            <div className="text-[10px] text-slate-400 font-medium mt-0.5">
                              {m.phone}
                            </div>
                          )}
                        </div>
                        {m.email && (
                        <button
                          type="button"
                          onClick={() => {
                            const details = getIndividualEmailDetails(entry, m);
                            onComposeEmail(
                              m.email,
                              m.name,
                              details.subject,
                              details.body,
                              entry.id,
                            );
                          }}
                          className="ml-2 p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex-shrink-0"
                        >
                          <EnvelopeIcon className="w-3.5 h-3.5" />
                        </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const details = getGroupEmailDetails(entry, members);
                      const emails = members.map((x) => x.email).join(", ");
                      const names = members.map((x) => x.name).join(", ");
                      onComposeEmail(
                        emails,
                        names,
                        details.subject,
                        details.body,
                        entry.id,
                      );
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-bold rounded-lg transition-all"
                  >
                    <PaperAirplaneIcon className="w-3 h-3" />
                    Email Group
                  </button>
                  <a
                    href={`/renewal-inbox/${entry.id}`}
                    className="inline-flex items-center justify-center gap-1.5 py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded-lg transition-all"
                  >
                    <EnvelopeIcon className="w-3 h-3" />
                    Open Inbox
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Comparable Units */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center">
              <div className="text-xs font-bold text-slate-700 flex items-center gap-2">
                <HomeIcon className="w-4 h-4 text-slate-500" />
                Comparable Units
              </div>
              {comparables.length > 0 && (
                <span className="text-[9px] font-extrabold bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full">
                  {comparables.length}
                </span>
              )}
            </div>
            {comparables.length > 0 ? (
              <div className="p-3 space-y-2">
                {visibleComparables.map((c) => {
                  const cc = STAGE_CFG[c.stage];
                  return (
                    <div
                      key={c.id}
                      className="bg-slate-50 rounded-xl border border-slate-200 p-3 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-extrabold text-slate-800 truncate">
                            {c.unit}
                          </div>
                          {c.unitLine && (
                            <div className="text-[9px] text-slate-500 font-bold mt-0.5">
                              {c.unitLine} Line
                            </div>
                          )}
                          <div className="text-[10px] text-slate-600 font-medium truncate mt-0.5">
                            {c.tenant || "—"}
                          </div>
                        </div>
                        <span
                          className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase border ${cc.bg} ${cc.text} ${cc.border}`}
                        >
                          {cc.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] border-t border-slate-200 pt-2">
                        <span className="text-slate-400 font-semibold">
                          Rent
                        </span>
                        <div className="text-slate-700 font-bold flex items-center gap-1">
                          <span>${c.rent.toLocaleString()}</span>
                          <span className="text-slate-400">→</span>
                          <span className="text-blue-600 font-extrabold">
                            {c.newRent > 0
                              ? `$${c.newRent.toLocaleString()}`
                              : "—"}
                          </span>
                          {c.newRent > 0 &&
                            c.rent > 0 &&
                            c.newRent > c.rent && (
                              <span className="text-[8px] font-extrabold ml-1 text-green-600">
                                +
                                {(
                                  ((c.newRent - c.rent) / c.rent) *
                                  100
                                ).toFixed(1)}
                                %
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {comparables.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllComparables(!showAllComparables)}
                    className="w-full text-center text-[10px] font-bold text-blue-600 hover:text-blue-800 py-2 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {showAllComparables
                      ? "Show Less"
                      : `View All Comparables (${comparables.length})`}
                  </button>
                )}
              </div>
            ) : (
              <div className="px-4 py-6 text-center">
                <BuildingOfficeIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-xs font-semibold text-slate-400">
                  No comparable units found
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Assign a Unit Line or Group to compare
                </p>
              </div>
            )}
          </div>

          {/* Marketing Stats */}
          {entry.showingStats && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-xs font-bold text-slate-700 flex items-center gap-2">
                  <EyeIcon className="w-4 h-4 text-slate-500" />
                  Marketing &amp; Showings
                </div>
              </div>
              <div className="p-3 grid grid-cols-3 gap-2">
                {[
                  {
                    label: "Future",
                    val: entry.showingStats.futureShowings,
                    color: "text-blue-600 bg-blue-50 border-blue-100",
                  },
                  {
                    label: "Past",
                    val: entry.showingStats.pastShowings,
                    color: "text-slate-600 bg-slate-50 border-slate-100",
                  },
                  {
                    label: "Apps In",
                    val: entry.showingStats.applicationsIn,
                    color: "text-amber-600 bg-amber-50 border-amber-100",
                  },
                  {
                    label: "Complete",
                    val: entry.showingStats.completeApplications,
                    color: "text-emerald-600 bg-emerald-50 border-emerald-100",
                  },
                  {
                    label: "Paid Apps",
                    val: entry.showingStats.completeWithPayment,
                    color: "text-indigo-600 bg-indigo-50 border-indigo-100",
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    className={`rounded-lg border p-2 text-center ${s.color}`}
                  >
                    <div className="text-base font-black">{s.val}</div>
                    <div className="text-[8px] font-extrabold uppercase text-slate-500 mt-0.5">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── FIXED FOOTER ── */}
        <div className="flex-shrink-0 bg-white border-t border-slate-200 px-5 py-4 space-y-3">
          <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium">
            <span>
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSaveAndClose}
              className="flex-1 py-2.5 text-sm font-extrabold text-white bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg transition-all"
            >
              <CheckIcon className="w-4 h-4" />
              Save &amp; Close
            </button>
          </div>
        </div>
      </div>

      {/* Create Custom Line Modal */}
      {showCreateLineModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white border border-slate-200 shadow-2xl rounded-2xl w-full max-w-sm flex flex-col overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                New Unit Line
              </h3>
              <button
                onClick={() => setShowCreateLineModal(false)}
                className="text-slate-400 hover:text-slate-655 p-1 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                Enter a custom unit line identifier for this building (e.g. "D",
                "16"). This is used to group units vertically.
              </p>
              <input
                type="text"
                value={customLineInput}
                onChange={(e) => setCustomLineInput(e.target.value)}
                placeholder="e.g. D Line"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const trimmed = customLineInput.trim();
                    if (trimmed) {
                      setUnitLine(trimmed);
                      if (onAddCustomLine) {
                        onAddCustomLine(entry.building, trimmed);
                      }
                    }
                    setShowCreateLineModal(false);
                  }
                }}
              />
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setShowCreateLineModal(false)}
                className="px-3 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const trimmed = customLineInput.trim();
                  if (trimmed) {
                    setUnitLine(trimmed);
                    if (onAddCustomLine) {
                      onAddCustomLine(entry.building, trimmed);
                    }
                  }
                  setShowCreateLineModal(false);
                }}
                className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Custom Group Modal */}
      {showCreateGroupModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white border border-slate-200 shadow-2xl rounded-2xl w-full max-w-sm flex flex-col overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                New Comparable Group
              </h3>
              <button
                onClick={() => setShowCreateGroupModal(false)}
                className="text-slate-400 hover:text-slate-655 p-1 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[10px] text-slate-500 font-semibold leading-relaxed">
                Create a new pricing comparable group for this building layout
                (e.g. "Penthouse Group", "2 Bedroom Line").
              </p>
              <input
                type="text"
                value={customGroupInput}
                onChange={(e) => setCustomGroupInput(e.target.value)}
                placeholder="e.g. D Line Group"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const trimmed = customGroupInput.trim();
                    if (trimmed) {
                      setComparableGroup(trimmed);
                      if (onAddCustomGroup) {
                        onAddCustomGroup(entry.building, trimmed);
                      }
                    }
                    setShowCreateGroupModal(false);
                  }
                }}
              />
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setShowCreateGroupModal(false)}
                className="px-3 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const trimmed = customGroupInput.trim();
                  if (trimmed) {
                    setComparableGroup(trimmed);
                    if (onAddCustomGroup) {
                      onAddCustomGroup(entry.building, trimmed);
                    }
                  }
                  setShowCreateGroupModal(false);
                }}
                className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Calendar View (shared: stages 1 and 3c, and main view) ────────────────────

function CalendarView({
  data,
  year,
  month,
  onEntryClick,
}: {
  data: RentalRollEntry[];
  year: number;
  month: number;
  onEntryClick: (e: RentalRollEntry) => void;
}) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDay = useMemo(() => {
    const map: Record<number, RentalRollEntry[]> = {};
    data.forEach((e) => {
      if (!e.vacateDate && !e.leaseTo) return;
      const d = parseDate(e.vacateDate || e.leaseTo);
      if (d && d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(e);
      }
    });
    return map;
  }, [data, year, month]);

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  const isToday = (d: number) =>
    TODAY.getFullYear() === year &&
    TODAY.getMonth() === month &&
    TODAY.getDate() === d;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto scrollbar-thin flex-1">
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200 text-gray-500 text-xs font-semibold uppercase tracking-wider">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-center py-3">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 border-l border-t border-gray-100">
        {cells.map((day, i) => {
          const entries = day ? byDay[day] || [] : [];
          const today = day !== null && isToday(day);
          return (
            <div
              key={i}
              className={`border-r border-b border-gray-100 min-h-[72px] p-1.5 flex flex-col justify-between transition-all duration-200 hover:bg-slate-50/40 hover:shadow-[inset_0_0_0_1.5px_rgba(59,130,246,0.08)] ${day === null ? "bg-slate-50/20" : "bg-white"}`}
            >
              {day !== null && (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <div
                      className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                        today
                          ? "bg-gradient-to-tr from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20"
                          : "text-slate-700 bg-slate-100/50"
                      }`}
                    >
                      {day}
                    </div>
                    {entries.length > 0 && (
                      <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-500 border border-slate-200/60 shadow-2xs">
                        {entries.length}{" "}
                        {entries.length === 1 ? "unit" : "units"}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5 flex-1">
                    {entries.slice(0, 2).map((e) => {
                      const sc = STAGE_CFG[e.stage];
                      const compSuffix = e.unitLine ? `(${e.unitLine})` : "";
                      const showingTooltip = e.showingStats
                        ? ` | Showings: ${e.showingStats.futureShowings} upcoming, ${e.showingStats.applicationsIn} apps`
                        : "";
                      return (
                        <button
                          key={e.id}
                          onClick={() => onEntryClick(e)}
                          className={`w-full text-left text-[9px] px-1.5 py-0.5 rounded-md truncate font-bold ${sc.bg} ${sc.text} border border-slate-100 border-l-4 ${sc.borderL} shadow-2xs hover:translate-x-0.5 hover:shadow-xs hover:brightness-95 active:scale-98 transition-all duration-150 ease-out`}
                          title={`${e.building ? `${e.building} - ` : ""}${e.unit} ${e.unitLine ? `(Line ${e.unitLine})` : ""} — ${e.tenant || "—"} (${sc.label})${showingTooltip}`}
                        >
                          {e.building ? `${e.building} - ` : ""}
                          {e.unit}
                          {compSuffix} {(e.tenant || "—").split(" ")[0]}
                        </button>
                      );
                    })}
                    {entries.length > 2 && (
                      <span className="text-[9px] text-slate-400 pl-1 font-extrabold block mt-0.5">
                        +{entries.length - 2} more
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Month List View (Month-scoped List grouped by building + collapsible status filter)

function MonthListView({
  data,
  year,
  month,
  onEntryClick,
  onMoveToMarketing,
  onMoveToRented,
  onComposeEmail,
}: {
  data: RentalRollEntry[];
  year: number;
  month: number;
  onEntryClick: (e: RentalRollEntry) => void;
  onMoveToMarketing: (id: string) => void;
  onMoveToRented: (id: string) => void;
  onComposeEmail: (
    recipients: string,
    names: string,
    subject: string,
    body: string,
    renewalId?: string,
  ) => void;
}) {
  const monthEntries = useMemo(
    () =>
      data.filter((e) => {
        if (!e.vacateDate && !e.leaseTo) return false;
        const d = parseDate(e.vacateDate || e.leaseTo);
        return d !== null && d.getFullYear() === year && d.getMonth() === month;
      }),
    [data, year, month],
  );

  const groupedByBuilding = useMemo(() => {
    const groups: Record<string, RentalRollEntry[]> = {};
    monthEntries.forEach((e) => {
      if (!groups[e.building]) {
        groups[e.building] = [];
      }
      groups[e.building].push(e);
    });
    return groups;
  }, [monthEntries]);

  const sortedBuildings = useMemo(() => {
    return Object.keys(groupedByBuilding).sort();
  }, [groupedByBuilding]);

  const [collapsedBuildings, setCollapsedBuildings] = useState<
    Record<string, boolean>
  >({});

  const toggleBuilding = (building: string) => {
    setCollapsedBuildings((prev) => ({
      ...prev,
      [building]: !prev[building],
    }));
  };

  const [activePopupId, setActivePopupId] = useState<string | null>(null);
  const [popupDirection, setPopupDirection] = useState<"above" | "below">(
    "below",
  );
  const [popupCoords, setPopupCoords] = useState<{ top: number; left: number }>(
    { top: 0, left: 0 },
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    entryId: string,
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const showAbove = spaceBelow < 370 && spaceAbove > spaceBelow;

    setActivePopupId(entryId);
    setPopupDirection(showAbove ? "above" : "below");
    setPopupCoords({
      top: showAbove ? rect.top + scrollY : rect.bottom + scrollY,
      left: rect.left + scrollX,
    });
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActivePopupId(null);
    }, 250);
  };

  const handlePopupMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  type SortField =
    | "unit"
    | "tenant"
    | "leaseTo"
    | "newRent"
    | "deposit"
    | "lateCount";
  type SortOrder = "asc" | "desc";
  interface SortRule {
    field: SortField;
    order: SortOrder;
  }

  const [sortRules, setSortRules] = useState<SortRule[]>([]);

  const handleSort = (field: SortField) => {
    setSortRules((prev) => {
      const idx = prev.findIndex((r) => r.field === field);
      if (idx === -1) {
        return [...prev, { field, order: "asc" }];
      } else {
        const currentRule = prev[idx];
        if (currentRule.order === "asc") {
          const next = [...prev];
          next[idx] = { field, order: "desc" };
          return next;
        } else {
          return prev.filter((r) => r.field !== field);
        }
      }
    });
  };

  const getSortIndicator = (field: SortField) => {
    const idx = sortRules.findIndex((r) => r.field === field);
    if (idx === -1) return "↕";
    const arrow = sortRules[idx].order === "asc" ? "▲" : "▼";
    if (sortRules.length > 1) {
      return `${arrow} (${idx + 1})`;
    }
    return arrow;
  };

  const sortEntries = useCallback(
    (entries: RentalRollEntry[]) => {
      if (sortRules.length === 0) return entries;

      return [...entries].sort((a, b) => {
        for (const rule of sortRules) {
          const { field, order } = rule;
          let valA: any = (a as any)[field];
          let valB: any = (b as any)[field];

          let comparison = 0;

          if (field === "unit") {
            const numA = parseInt(a.unit.replace(/\D/g, "")) || 0;
            const numB = parseInt(b.unit.replace(/\D/g, "")) || 0;
            if (numA !== numB) {
              comparison = numA - numB;
            } else {
              comparison = a.unit.localeCompare(b.unit);
            }
          } else if (field === "leaseTo") {
            const dateA = parseDate(a.leaseTo)?.getTime() ?? 0;
            const dateB = parseDate(b.leaseTo)?.getTime() ?? 0;
            comparison = dateA - dateB;
          } else if (field === "deposit") {
            comparison = a.deposit - b.deposit;
          } else if (field === "newRent" || field === "lateCount") {
            comparison = valA - valB;
          } else {
            const strA = String(valA || "").toLowerCase();
            const strB = String(valB || "").toLowerCase();
            comparison = strA.localeCompare(strB);
          }

          if (comparison !== 0) {
            return order === "asc" ? comparison : -comparison;
          }
        }
        return 0;
      });
    },
    [sortRules],
  );

  const renderRow = (e: RentalRollEntry, showBuildingName: boolean = false) => {
    const leaseStart = parseDate(e.leaseFrom);
    const leaseEnd = parseDate(e.leaseTo);

    return (
      <tr
        key={e.id}
        className={`${STAGE_CFG[e.stage].rowBg} transition-all h-7`}
      >
        <td
          className="px-4 py-0.5 font-semibold text-gray-900 align-middle"
          style={{
            borderLeftWidth: "3px",
            borderLeftColor: STAGE_CFG[e.stage].color,
            borderLeftStyle: "solid",
          }}
        >
          <div className="font-semibold text-xs">
            {e.unit} {e.unitLine ? `(Line ${e.unitLine})` : ""}
          </div>
          {showBuildingName && (
            <div className="text-[9px] text-slate-400 mt-0 font-bold uppercase tracking-wider">
              {e.building}
            </div>
          )}
        </td>
        <td
          className={`px-4 py-0.5 relative transition-colors align-middle ${activePopupId === e.id ? "z-50" : "z-20"} whitespace-nowrap`}
        >
          <div
            onMouseEnter={
              e.tenant ? (event) => handleMouseEnter(event, e.id) : undefined
            }
            onMouseLeave={e.tenant ? handleMouseLeave : undefined}
            className={
              e.tenant ? "cursor-pointer inline-flex items-center gap-1" : ""
            }
          >
            <div
              className={`font-semibold text-gray-900 ${e.tenant ? "hover:text-blue-600" : ""} transition-colors flex items-center gap-1 justify-start`}
            >
              <span
                className={`transition-colors ${activePopupId === e.id ? "text-blue-600" : e.tenant ? "text-gray-900" : "text-gray-400 font-normal italic"}`}
              >
                {e.tenant ? e.tenant.split(",")[0].trim() : "—"}
              </span>
              {e.tenant && (
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${activePopupId === e.id ? "text-blue-600" : "text-gray-400"}`}
                />
              )}
            </div>
          </div>

          {/* Hover Popup Card */}
          {activePopupId === e.id &&
            createPortal(
              <div
                onMouseEnter={handlePopupMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  position: "absolute",
                  top: `${popupCoords.top}px`,
                  left: `${popupCoords.left}px`,
                  transform: popupDirection === "above" ? "translateY(-100%) translateY(-4px)" : "translateY(4px)",
                }}
                className={`z-[9999] w-72 bg-white opacity-100 shadow-2xl border border-gray-200 p-4 text-left space-y-3.5 ${popupDirection === "above" ? "rounded-t-xl rounded-r-xl" : "rounded-b-xl rounded-r-xl"}`}
              >
                {/* Card Header */}
                <div className="border-b border-gray-100 pb-2">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Lease Group Members
                  </h4>
                </div>

                {/* Member List */}
                <div className="max-h-[195px] overflow-y-auto pr-1 scrollbar-thin divide-y divide-gray-100">
                  {getGroupMembers(e).length > 0 ? (
                    getGroupMembers(e).map((m, idx) => {
                      const roleBracket = m.isGuarantor
                        ? "(Guarantor)"
                        : "(Tenant)";
                      return (
                        <div
                          key={idx}
                          className="flex justify-between items-center py-2 px-1 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-bold text-gray-900">
                                {m.name}
                              </span>
                              <span className="text-[10px] text-gray-400 font-extrabold">
                                {roleBracket}
                              </span>
                            </div>
                            {m.email ? (
                              <div className="text-[10px] text-gray-500 truncate mt-0.5">
                                {m.email}
                              </div>
                            ) : (
                              <div className="text-[10px] text-gray-300 italic mt-0.5">
                                No email on file
                              </div>
                            )}
                            {m.phone && (
                              <div className="text-[10px] text-gray-400 truncate mt-0.5">
                                {m.phone}
                              </div>
                            )}
                          </div>
                          {m.email && (
                          <button
                            type="button"
                            onClick={() => {
                              const details = getIndividualEmailDetails(e, m);
                              onComposeEmail(
                                m.email,
                                m.name,
                                details.subject,
                                details.body,
                                e.id,
                              );
                            }}
                            title={`Email ${m.name}`}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                          >
                            <EnvelopeIcon className="w-3.5 h-3.5" />
                          </button>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-6 text-center text-xs font-semibold text-gray-400">
                      No group members
                    </div>
                  )}
                </div>

                {/* Showings & Applications (if any) */}
                {e.showingStats && (
                  <div className="pt-2.5 border-t border-gray-100">
                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Showings & Applications
                    </h4>
                    <div className="grid grid-cols-5 gap-1 text-center">
                      <div className="bg-blue-50 border border-blue-100 rounded p-1">
                        <div className="text-xs font-bold text-blue-700">
                          {e.showingStats.futureShowings}
                        </div>
                        <div className="text-[8px] text-gray-400 font-bold uppercase leading-none mt-0.5">
                          Future
                        </div>
                      </div>
                      <div className="bg-slate-50 border border-slate-100 rounded p-1">
                        <div className="text-xs font-bold text-slate-700">
                          {e.showingStats.pastShowings}
                        </div>
                        <div className="text-[8px] text-gray-400 font-bold uppercase leading-none mt-0.5">
                          Past
                        </div>
                      </div>
                      <div className="bg-amber-50 border border-amber-100 rounded p-1">
                        <div className="text-xs font-bold text-amber-700">
                          {e.showingStats.applicationsIn}
                        </div>
                        <div className="text-[8px] text-gray-400 font-bold uppercase leading-none mt-0.5">
                          Apps
                        </div>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 rounded p-1">
                        <div className="text-xs font-bold text-emerald-700">
                          {e.showingStats.completeApplications}
                        </div>
                        <div className="text-[8px] text-gray-400 font-bold uppercase leading-none mt-0.5">
                          Done
                        </div>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-100 rounded p-1">
                        <div className="text-xs font-bold text-indigo-700">
                          {e.showingStats.completeWithPayment}
                        </div>
                        <div className="text-[8px] text-gray-400 font-bold uppercase leading-none mt-0.5">
                          Paid
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-100 flex flex-col gap-1.5">
                  {getGroupMembers(e).length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const members = getGroupMembers(e);
                        const details = getGroupEmailDetails(e, members);
                        const emails = members.map((x) => x.email).join(", ");
                        const names = members.map((x) => x.name).join(", ");
                        onComposeEmail(
                          emails,
                          names,
                          details.subject,
                          details.body,
                          e.id,
                        );
                      }}
                      className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors shadow-sm"
                    >
                      <PaperAirplaneIcon className="w-3.5 h-3.5 text-slate-550" />
                      Email All Group Members
                    </button>
                  )}
                  <a
                    href={`/renewal-inbox/${e.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm"
                  >
                    <EnvelopeIcon className="w-3.5 h-3.5" />
                    Open Inbox
                  </a>
                </div>
              </div>,
              document.body,
            )}
        </td>
        <td className="px-4 py-0.5 text-xs align-middle whitespace-nowrap">
          <div className="font-medium text-gray-800 flex items-center gap-1.5 whitespace-nowrap">
            <span className="whitespace-nowrap">
              {leaseStart
                ? leaseStart.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "2-digit",
                  })
                : "—"}{" "}
              -{" "}
              {leaseEnd
                ? leaseEnd.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "2-digit",
                  })
                : "—"}
            </span>
            <span className="text-[10px] text-gray-400 font-normal whitespace-nowrap">
              (Current: ${e.rent.toLocaleString()}
              {e.vacateDate && parseDate(e.vacateDate)
                ? ` · Vac: ${parseDate(e.vacateDate)!.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}`
                : ""}
              )
            </span>
            {e.hasStatusMismatch && (
              <span className="text-[9px] text-amber-700 font-semibold ml-1 bg-amber-50 px-1 rounded whitespace-nowrap">
                Src: {e.sourceStatus || e.sourceMappedStatus}
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-0.5 text-xs text-center font-semibold text-gray-900 align-middle">
          {e.newRent > 0 ? `$${e.newRent.toLocaleString()}` : "—"}
        </td>
        <td className="px-4 py-0.5 text-center align-middle">
          <span
            className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.2 rounded-full text-[10px] font-bold ${e.lateCount > 0 ? "bg-red-50 text-red-700 border border-red-200" : "bg-gray-50 text-gray-400 border border-gray-200"}`}
          >
            {e.lateCount}
          </span>
        </td>
        <td className="pl-2 pr-4 py-0.5 text-right align-middle w-40">
          <div className="flex justify-end gap-1.5">
            {e.stage === "not_renewing" && (
              <button
                onClick={() => onMoveToMarketing(e.id)}
                className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap"
              >
                <PaperAirplaneIcon className="w-2.5 h-2.5" />
                Market
              </button>
            )}
            {e.stage === "being_marketed" && (
              <button
                onClick={() => onMoveToRented(e.id)}
                className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap"
              >
                <CheckBadgeIcon className="w-2.5 h-2.5" />
                Rented
              </button>
            )}
            <button
              onClick={() => onEntryClick(e)}
              className="px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-755 text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap"
            >
              Details
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-6">
      {monthEntries.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-16 text-center">
          <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            No units match the selected filters for {MONTHS[month]} {year}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm relative overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="overflow-auto scrollbar-thin flex-1">
            <table className="w-full text-left text-sm text-gray-500">
              <thead className="sticky top-0 text-[11px] text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-150 z-30">
                <tr>
                  <th
                    scope="col"
                    onClick={() => handleSort("unit")}
                    className="px-4 py-1 font-semibold cursor-pointer select-none hover:bg-slate-100/40 transition-colors sticky top-0 bg-slate-50 z-30 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1.5 justify-start">
                      <span>Unit / Line</span>
                      <span className="text-[9px] text-slate-400 font-bold">
                        {getSortIndicator("unit")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    onClick={() => handleSort("tenant")}
                    className="px-4 py-1 font-semibold cursor-pointer select-none hover:bg-slate-100/40 transition-colors sticky top-0 bg-slate-50 z-30 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1.5 justify-start">
                      <span>Tenant</span>
                      <span className="text-[9px] text-slate-400 font-bold">
                        {getSortIndicator("tenant")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    onClick={() => handleSort("leaseTo")}
                    className="px-4 py-1 font-semibold cursor-pointer select-none hover:bg-slate-100/40 transition-colors sticky top-0 bg-slate-50 z-30 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1.5 justify-start">
                      <span>Lease Period</span>
                      <span className="text-[9px] text-slate-400 font-bold">
                        {getSortIndicator("leaseTo")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    onClick={() => handleSort("newRent")}
                    className="px-4 py-1 font-semibold text-center cursor-pointer select-none hover:bg-slate-100/40 transition-colors sticky top-0 bg-slate-50 z-30 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1.5 justify-center">
                      <span>Proposed Rent</span>
                      <span className="text-[9px] text-slate-400 font-bold">
                        {getSortIndicator("newRent")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    onClick={() => handleSort("lateCount")}
                    className="px-4 py-1 font-semibold text-center cursor-pointer select-none hover:bg-slate-100/40 transition-colors sticky top-0 bg-slate-50 z-30 whitespace-nowrap"
                  >
                    <div className="flex items-center gap-1.5 justify-center">
                      <span>Late Payments</span>
                      <span className="text-[9px] text-slate-400 font-bold">
                        {getSortIndicator("lateCount")}
                      </span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="pl-2 pr-4 py-1 font-semibold text-right w-40 sticky top-0 bg-slate-50 z-30 whitespace-nowrap"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {sortedBuildings.map((building, idx) => {
                  const buildingAddress =
                    groupedByBuilding[building][0]?.buildingAddress || "";
                  const isCollapsed = collapsedBuildings[building];
                  return (
                    <React.Fragment key={building}>
                      {/* Building Section Header Row */}
                      <tr
                        onClick={() => toggleBuilding(building)}
                        className="bg-slate-50/80 border-y border-slate-200 cursor-pointer select-none hover:bg-slate-100 transition-colors h-7"
                      >
                        <td
                          colSpan={6}
                          className="px-4 py-0.5 font-bold text-slate-800 text-xs"
                        >
                          <div className="flex items-center gap-2">
                            {/* Collapse indicator chevron at the very front */}
                            <span className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0">
                              {isCollapsed ? (
                                <ChevronRightIcon className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronDownIcon className="w-3.5 h-3.5" />
                              )}
                            </span>

                            {/* Building Office Icon */}
                            <BuildingOfficeIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />

                            {/* Building details */}
                            <div className="text-left flex-1 min-w-0 flex items-center gap-1.5">
                              <span className="uppercase tracking-wider font-extrabold text-slate-900 text-[10.5px] truncate">
                                {building}
                              </span>
                              <span className="text-[9px] font-bold text-slate-500 bg-white border border-slate-200 px-1.5 py-0.2 rounded-full shadow-2xs">
                                {groupedByBuilding[building].length} Unit
                                {groupedByBuilding[building].length !== 1
                                  ? "s"
                                  : ""}
                              </span>
                              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider bg-slate-100 border border-slate-200/60 px-1.5 py-0.2 rounded-full">
                                {isCollapsed ? "Collapsed" : "Expanded"}
                              </span>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {!isCollapsed &&
                        sortEntries(groupedByBuilding[building]).map((e) =>
                          renderRow(e, false),
                        )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Quarter View (shared) ──────────────────────────────────────────────────────

function QuarterView({
  data,
  year,
  quarter,
  onEntryClick,
}: {
  data: RentalRollEntry[];
  year: number;
  quarter: number;
  onEntryClick: (e: RentalRollEntry) => void;
}) {
  const months = [quarter * 3, quarter * 3 + 1, quarter * 3 + 2];
  const [collapsedBuildings, setCollapsedBuildings] = useState<
    Record<string, boolean>
  >({});

  const toggleBuilding = (monthIndex: number, building: string) => {
    const key = `${monthIndex}-${building}`;
    setCollapsedBuildings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-4">
      {months.map((m) => {
        const entries = data.filter((e) => {
          if (!e.vacateDate && !e.leaseTo) return false;
          const d = parseDate(e.vacateDate || e.leaseTo);
          return d !== null && d.getFullYear() === year && d.getMonth() === m;
        });

        // Group entries by building
        const groupedByBuilding: Record<string, RentalRollEntry[]> = {};
        entries.forEach((e) => {
          if (!groupedByBuilding[e.building]) {
            groupedByBuilding[e.building] = [];
          }
          groupedByBuilding[e.building].push(e);
        });
        const sortedBuildings = Object.keys(groupedByBuilding).sort();

        return (
          <div
            key={m}
            className="bg-gray-50/50 rounded-xl border border-gray-200 flex flex-col h-full overflow-hidden"
          >
            {/* Month Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">{MONTHS[m]}</h3>
                <p className="text-[10px] text-gray-400 font-medium uppercase mt-0.5">
                  {year} Expirations
                </p>
              </div>
              <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">
                {entries.length} Unit{entries.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Content List */}
            <div className="overflow-y-auto flex-1 scrollbar-thin">
              {entries.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 text-gray-400">
                  <CalendarDaysIcon className="w-8 h-8 text-gray-300 mb-2" />
                  <p className="text-xs">No expirations this month</p>
                </div>
              ) : (
                sortedBuildings.map((building) => {
                  const isCollapsed = collapsedBuildings[`${m}-${building}`];
                  return (
                    <div
                      key={building}
                      className="border-b border-slate-100 last:border-b-0 bg-white"
                    >
                      {/* Building Section Header */}
                      <div
                        onClick={() => toggleBuilding(m, building)}
                        className="sticky top-0 bg-slate-100 border-b border-slate-200/80 px-4 py-1.5 flex items-center justify-between z-10 shadow-sm cursor-pointer select-none hover:bg-slate-200/80 transition-colors h-7"
                      >
                        <div className="flex items-center gap-1.5">
                          {/* Collapse indicator chevron */}
                          <span className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0">
                            {isCollapsed ? (
                              <ChevronRightIcon className="w-3 h-3" />
                            ) : (
                              <ChevronDownIcon className="w-3 h-3" />
                            )}
                          </span>
                          <BuildingOfficeIcon className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          <span className="truncate font-extrabold text-slate-800 uppercase tracking-wider text-[10px]">
                            {building}
                          </span>
                        </div>
                        <span className="text-[9px] font-bold text-slate-500 bg-white border border-slate-200 px-1.5 py-0.2 rounded-full shadow-2xs">
                          {groupedByBuilding[building].length} Unit
                          {groupedByBuilding[building].length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Rows Container */}
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-100 border-t border-gray-150">
                          {groupedByBuilding[building].map((e) => {
                            const sc = STAGE_CFG[e.stage];

                            return (
                              <div
                                key={e.id}
                                onClick={() => onEntryClick(e)}
                                className={`group border-b border-gray-150 px-3 py-2 cursor-pointer transition-all duration-150 flex items-center justify-between text-xs gap-3 ${sc.rowBg} hover:brightness-95`}
                                style={{
                                  borderLeftWidth: "3px",
                                  borderLeftColor: sc.color,
                                }}
                              >
                                {/* Unit Number (Shown completely, no fixed width truncation) */}
                                <span
                                  className={`font-bold text-xs flex-shrink-0 ${sc.text}`}
                                >
                                  {e.unit}
                                </span>

                                {/* Lease End Date & Status Badge */}
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span
                                    className={`text-[10px] font-medium whitespace-nowrap opacity-80 ${sc.text}`}
                                  >
                                    {e.vacateDate ? "Vac" : "Exp"}{" "}
                                    {parseDate(
                                      e.vacateDate || e.leaseTo,
                                    )!.toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </span>
                                  <span
                                    className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded border bg-white ${sc.text} ${sc.border} shadow-2xs`}
                                  >
                                    {sc.label}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Outreach Queue View (Proactive Outreach Scheduling) ──────────────────────

interface OutreachQueueViewProps {
  data: RentalRollEntry[];
  onEntryClick: (e: RentalRollEntry) => void;
  onMoveToMarketing: (id: string) => void;
  onMoveToRented: (id: string) => void;
  onComposeEmail: (
    recipients: string,
    names: string,
    subject: string,
    body: string,
    renewalId?: string,
  ) => void;
}

const OutreachQueueView: React.FC<OutreachQueueViewProps> = ({
  data,
  onEntryClick,
  onMoveToMarketing,
  onMoveToRented,
  onComposeEmail,
}) => {
  const [activeTab, setActiveTab] = useState<
    "all" | "action" | "upcoming" | "processed"
  >("action");

  // Compute values for each item in the data set

  const queueItems = useMemo(() => {
    return data.map((e) => {
      const years = tenancyYears(e.leaseFrom);
      const lead = outreachWindow(e.leaseFrom);
      const outreachDate = getOutreachTargetDate(e.leaseTo, e.leaseFrom);
      const daysUntil = getDaysUntilOutreach(e.leaseTo, e.leaseFrom);

      let status: "overdue" | "due" | "upcoming" | "processed" = "upcoming";
      if (e.stage !== "up_for_renewal") {
        status = "processed";
      } else if (daysUntil < 0) {
        status = "overdue";
      } else if (daysUntil <= 7) {
        status = "due";
      }

      return {
        entry: e,
        years,
        lead,
        outreachDate,
        daysUntil,
        status,
      };
    });
  }, [data]);

  // Calculate Metrics based on compute values

  const metrics = useMemo(() => {
    let overdue = 0;
    let due = 0;
    let upcoming = 0;
    let processed = 0;

    queueItems.forEach((item) => {
      if (item.status === "processed") processed++;
      else if (item.status === "overdue") overdue++;
      else if (item.status === "due") due++;
      else upcoming++;
    });

    return { overdue, due, upcoming, processed };
  }, [queueItems]);

  // Filter queue items by active tab

  const filteredQueueItems = useMemo(() => {
    const items = queueItems.filter((item) => {
      if (activeTab === "action") {
        return item.status === "overdue" || item.status === "due";
      }
      if (activeTab === "upcoming") {
        return item.status === "upcoming";
      }
      if (activeTab === "processed") {
        return item.status === "processed";
      }
      return true; // "all"
    });

    // Sort by outreach target date (earliest/most overdue first)
    return items.sort((a, b) => a.daysUntil - b.daysUntil);
  }, [queueItems, activeTab]);

  return (
    <div className="flex-1 flex flex-col min-h-0 space-y-6">
      {/* Metrics Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Overdue */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Overdue Outreach
          </span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-3xl font-bold text-rose-600">
              {metrics.overdue}
            </span>
            <span className="text-xs text-rose-500 font-medium">pending</span>
          </div>
          <div className="w-full h-1 bg-gray-100 rounded-full mt-3 overflow-hidden">
            <div
              className="h-full bg-rose-500"
              style={{
                width: `${metrics.overdue ? Math.min(100, (metrics.overdue / queueItems.length) * 100) : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Due Now */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Due This Week
          </span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-3xl font-bold text-amber-600">
              {metrics.due}
            </span>
            <span className="text-xs text-amber-500 font-medium">ready</span>
          </div>
          <div className="w-full h-1 bg-gray-100 rounded-full mt-3 overflow-hidden">
            <div
              className="h-full bg-amber-500"
              style={{
                width: `${metrics.due ? Math.min(100, (metrics.due / queueItems.length) * 100) : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Upcoming */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Upcoming (30d)
          </span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-3xl font-bold text-indigo-600">
              {metrics.upcoming}
            </span>
            <span className="text-xs text-indigo-500 font-medium">
              in queue
            </span>
          </div>
          <div className="w-full h-1 bg-gray-100 rounded-full mt-3 overflow-hidden">
            <div
              className="h-full bg-indigo-500"
              style={{
                width: `${metrics.upcoming ? Math.min(100, (metrics.upcoming / queueItems.length) * 100) : 0}%`,
              }}
            />
          </div>
        </div>

        {/* Processed */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Outreach Sent
          </span>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-3xl font-bold text-emerald-600">
              {metrics.processed}
            </span>
            <span className="text-xs text-emerald-500 font-medium">sent</span>
          </div>
          <div className="w-full h-1 bg-gray-100 rounded-full mt-3 overflow-hidden">
            <div
              className="h-full bg-emerald-500"
              style={{
                width: `${metrics.processed ? Math.min(100, (metrics.processed / queueItems.length) * 100) : 0}%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Tabs Switcher and Table Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex-1 flex flex-col min-h-0">
        {/* Header Tabs */}
        <div className="border-b border-gray-100 bg-gray-50/50 px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {[
              {
                id: "action",
                label: "Action Required",
                count: metrics.overdue + metrics.due,
                color: "text-red-700 bg-red-50 border-red-200",
              },
              {
                id: "upcoming",
                label: "Upcoming Queue",
                count: metrics.upcoming,
                color: "text-blue-700 bg-blue-50 border-blue-200",
              },
              {
                id: "processed",
                label: "Sent / Processed",
                count: metrics.processed,
                color: "text-green-700 bg-green-50 border-green-200",
              },
              {
                id: "all",
                label: "All Leases",
                count: queueItems.length,
                color: "text-gray-700 bg-gray-100 border-gray-300",
              },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id as any)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 transition-all ${
                  activeTab === t.id
                    ? `${t.color} shadow-sm ring-2 ring-offset-1 ring-blue-500`
                    : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span>{t.label}</span>
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/70 border border-current/20">
                  {t.count}
                </span>
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-400 font-medium">
            Sorted by Target Outreach Date
          </span>
        </div>

        {/* Table View */}
        {filteredQueueItems.length === 0 ? (
          <div className="p-16 text-center">
            <BuildingOfficeIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              No items found in this section
            </p>
          </div>
        ) : (
          <div className="overflow-auto scrollbar-thin flex-1">
            <table className="w-full text-left text-sm text-gray-500">
              <thead className="sticky top-0 text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200 z-30">
                <tr>
                  <th className="px-4 py-1 font-semibold sticky top-0 bg-gray-50 z-30">
                    Unit & Building
                  </th>
                  <th className="px-4 py-1 font-semibold w-60 max-w-[240px] sticky top-0 bg-gray-50 z-30">
                    Tenant & Tenancy
                  </th>
                  <th className="px-4 py-1 font-semibold sticky top-0 bg-gray-50 z-30">
                    Lead window
                  </th>
                  <th className="px-4 py-1 font-semibold sticky top-0 bg-gray-50 z-30">
                    Outreach target
                  </th>
                  <th className="px-4 py-1 font-semibold sticky top-0 bg-gray-50 z-30">
                    Lease expires
                  </th>
                  <th className="px-4 py-1 font-semibold text-right sticky top-0 bg-gray-50 z-30">
                    New Rent
                  </th>
                  <th className="px-4 py-1 text-center sticky top-0 bg-gray-50 z-30">
                    Status
                  </th>
                  <th className="pl-2 pr-4 py-1 font-semibold text-right sticky top-0 bg-gray-50 z-30">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredQueueItems.map((item) => {
                  const e = item.entry;
                  const dateStr = item.outreachDate
                    ? item.outreachDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—";
                  const expDate = parseDate(e.leaseTo);
                  const expStr = expDate
                    ? expDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—";

                  // Style outreach pill
                  let pillClass = "text-gray-700 bg-gray-50 border-gray-200";
                  let pillText = "";

                  if (item.status === "processed") {
                    pillClass = "text-green-700 bg-green-50 border-green-200";
                    pillText = "Completed";
                  } else if (item.status === "overdue") {
                    pillClass =
                      "text-red-700 bg-red-100 border-red-300 animate-pulse";
                    pillText = `${Math.abs(item.daysUntil)}d Overdue`;
                  } else if (item.status === "due") {
                    pillClass =
                      "text-orange-700 bg-orange-50 border-orange-200";
                    pillText =
                      item.daysUntil === 0
                        ? "Due Today"
                        : `Due in ${item.daysUntil}d`;
                  } else {
                    pillClass = "text-blue-700 bg-blue-50 border-blue-200";
                    pillText = `Starts in ${item.daysUntil}d`;
                  }

                  return (
                    <tr
                      key={e.id}
                      className={`${STAGE_CFG[e.stage].rowBg} hover:brightness-95 transition-all h-8`}
                    >
                      <td
                        className="px-4 py-0.5 font-bold text-gray-900 align-middle text-xs"
                        style={{
                          borderLeftWidth: "3px",
                          borderLeftColor: STAGE_CFG[e.stage].color,
                          borderLeftStyle: "solid",
                        }}
                      >
                        {e.unit}
                        {e.unitLine && (
                          <span className="ml-1.5 text-[9px] font-semibold text-gray-400 bg-gray-100 px-1 py-0.2 rounded">
                            Line {e.unitLine}
                          </span>
                        )}
                        <div
                          className="text-[10px] text-gray-400 font-normal mt-0 truncate max-w-[200px]"
                          title={e.building}
                        >
                          {e.building}
                        </div>
                      </td>
                      <td className="px-4 py-0.5 w-60 max-w-[240px] align-middle text-xs">
                        <div
                          className="font-semibold text-gray-900 truncate"
                          title={e.tenant || "—"}
                        >
                          {e.tenant || "—"}
                        </div>
                        <div className="text-[10px] text-gray-400 truncate">
                          {parseDate(e.leaseFrom) ? (
                            <>
                              Since{" "}
                              {parseDate(e.leaseFrom)!.toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                },
                              )}
                            </>
                          ) : (
                            "No start date"
                          )}{" "}
                          · {item.years.toFixed(1)} yr
                          {item.years !== 1 ? "s" : ""}
                        </div>
                      </td>
                      <td className="px-4 py-0.5 text-xs font-semibold text-gray-700 align-middle">
                        {item.lead} days
                        <div className="text-[9px] text-gray-400 font-normal mt-0">
                          {item.lead === 95
                            ? "2yr+ tenant rule"
                            : "< 2yr tenant rule"}
                        </div>
                      </td>
                      <td className="px-4 py-0.5 text-xs align-middle">
                        <div className="font-medium text-gray-800">
                          {dateStr}
                        </div>
                        <span
                          className={`inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-semibold border mt-0.5 ${pillClass}`}
                        >
                          {pillText}
                        </span>
                      </td>
                      <td className="px-4 py-0.5 text-xs font-medium text-gray-900 align-middle">
                        {expStr}
                      </td>
                      <td className="px-4 py-0.5 text-xs text-right font-semibold text-gray-900 align-middle">
                        ${e.newRent.toLocaleString()}
                      </td>
                      <td className="px-4 py-0.5 text-center align-middle">
                        <StageBadge stage={e.stage} />
                        {e.hasStatusMismatch && (
                          <div className="text-[9px] text-amber-700 font-semibold mt-0">
                            Source: {e.sourceStatus || e.sourceMappedStatus}
                          </div>
                        )}
                      </td>
                      <td className="pl-2 pr-4 py-1 text-right align-middle">
                        <div className="flex justify-end gap-1.5">
                          {e.stage === "not_renewing" && (
                            <button
                              onClick={() => onMoveToMarketing(e.id)}
                              className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-600 hover:bg-purple-700 text-white text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap"
                            >
                              <PaperAirplaneIcon className="w-2.5 h-2.5" />
                              Move to Marketing
                            </button>
                          )}
                          {e.stage === "being_marketed" && (
                            <button
                              onClick={() => onMoveToRented(e.id)}
                              className="flex items-center gap-1 px-1.5 py-0.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap"
                            >
                              <CheckBadgeIcon className="w-2.5 h-2.5" />
                              Move to Rented
                            </button>
                          )}
                          <button
                            onClick={() => onEntryClick(e)}
                            className="px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-750 text-[10px] font-semibold rounded-md transition-colors whitespace-nowrap"
                          >
                            Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Attention Panel ─────────────────────────────────────────────────────────────

interface AttentionPanelProps {
  items: RenewalAttentionApiEntry[];
  onEntryClick: (e: RentalRollEntry) => void;
  onComposeEmail: (to: string, name: string, subject: string, body: string, entryId: string) => void;
}

const AttentionPanel: React.FC<AttentionPanelProps> = ({
  items,
  onEntryClick,
  onComposeEmail,
}) => {
  const [noLeaseDateOpen, setNoLeaseDateOpen] = React.useState(true);
  const [expiredOpen, setExpiredOpen] = React.useState(true);

  const [activePopupId, setActivePopupId] = React.useState<string | null>(null);
  const [popupDirection, setPopupDirection] = React.useState<"above" | "below">(
    "below",
  );
  const [popupCoords, setPopupCoords] = React.useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    entryId: string,
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const showAbove = spaceBelow < 250 && spaceAbove > spaceBelow;

    setActivePopupId(entryId);
    setPopupDirection(showAbove ? "above" : "below");
    setPopupCoords({
      top: showAbove ? rect.top + scrollY : rect.bottom + scrollY,
      left: rect.left + scrollX,
    });
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActivePopupId(null);
    }, 250);
  };

  const handlePopupMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const noLeaseItems = items.filter((i) => i.attentionType === "no_lease_date");
  const expiredItems = items.filter((i) => i.attentionType === "expired");

  if (noLeaseItems.length === 0 && expiredItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-xl shadow-xs text-center mt-4">
        <CheckCircleIcon className="w-12 h-12 text-green-500 mb-2" />
        <h3 className="text-sm font-bold text-gray-800">No Attention Needed</h3>
        <p className="text-xs text-gray-400 mt-1 max-w-sm">
          All standard units have a lease end date set and are up to date.
        </p>
      </div>
    );
  }

  const renderRow = (item: RenewalAttentionApiEntry) => {
    const e = mapApiRenewal(item.entry);
    return (
      <tr
        key={e.id}
        className="hover:bg-amber-50/60 transition-colors cursor-pointer group"
        onClick={() => onEntryClick(e)}
      >
        <td className="px-3 py-1.5 text-xs font-semibold text-gray-800 whitespace-nowrap">
          {e.unit}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-600 whitespace-nowrap">
          {e.building}
        </td>
        <td
          className={`px-3 py-1.5 relative transition-colors align-middle ${activePopupId === e.id ? "z-50" : "z-20"} whitespace-nowrap text-xs`}
        >
          <div
            onMouseEnter={
              e.tenant ? (event) => handleMouseEnter(event, e.id) : undefined
            }
            onMouseLeave={e.tenant ? handleMouseLeave : undefined}
            className={
              e.tenant ? "cursor-pointer inline-flex items-center gap-1" : ""
            }
          >
            <div
              className={`font-semibold text-gray-900 ${e.tenant ? "hover:text-blue-600" : ""} transition-colors flex items-center gap-1 justify-start`}
            >
              <span
                className={`transition-colors ${activePopupId === e.id ? "text-blue-600" : e.tenant ? "text-gray-900" : "text-gray-400 font-normal italic"}`}
              >
                {e.tenant ? e.tenant.split(",")[0].trim() : "—"}
              </span>
              {e.tenant && (
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${activePopupId === e.id ? "text-blue-600" : "text-gray-400"}`}
                />
              )}
            </div>
          </div>
          {activePopupId === e.id &&
            e.tenant &&
            createPortal(
              <div
                onMouseEnter={handlePopupMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  position: "absolute",
                  top: `${popupCoords.top}px`,
                  left: `${popupCoords.left}px`,
                  transform: popupDirection === "above" ? "translateY(-100%) translateY(-4px)" : "translateY(4px)",
                }}
                className={`z-[9999] w-80 bg-white opacity-100 shadow-2xl border border-slate-200 p-4 text-left space-y-3.5 transition-all duration-200 scale-100 ${
                  popupDirection === "above" ? "rounded-t-xl rounded-r-xl" : "rounded-b-xl rounded-r-xl"
                }`}
              >
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                  <div className="flex items-center gap-2">
                    <UserGroupIcon className="w-5 h-5 text-blue-500" />
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        Occupants List
                      </h4>
                      <p className="text-[10px] text-slate-400">
                        {e.occupants?.length || 0} person(s) registered
                      </p>
                    </div>
                  </div>
                  <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-700 uppercase tracking-wider">
                    {e.unit}
                  </span>
                </div>

                <div className="space-y-2 max-h-[195px] overflow-y-auto pr-1 scrollbar-thin">
                  {e.occupants && e.occupants.length > 0 ? (
                    e.occupants.map((o) => (
                      <div
                        key={o.id}
                        className="flex flex-col p-2 bg-slate-50 rounded-lg hover:bg-slate-100/80 transition-colors border border-slate-100"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-800">
                            {o.name}
                          </span>
                          {o.isCosigner && (
                            <span className="text-[8px] font-bold px-1 py-0.2 rounded bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider">
                              Cosigner
                            </span>
                          )}
                        </div>
                        {o.email && (
                          <span className="text-[10px] text-slate-500 mt-0.5 break-all">
                            {o.email}
                          </span>
                        )}
                        {o.phone && (
                          <span className="text-[10px] text-slate-400 mt-0.5">
                            {o.phone}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-xs text-slate-400 italic">
                      No occupants listed
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                  {e.email && (
                    <button
                      onClick={() =>
                        onComposeEmail(
                          e.email,
                          e.tenant,
                          `Lease Renewal Request - Unit ${e.unit}`,
                          `Dear ${e.tenant},\n\nThis is a friendly reminder regarding your lease renewal for Unit ${e.unit}.\n\nBest regards,\nManagement`,
                          e.id
                        )
                      }
                      className="flex-1 inline-flex items-center justify-center gap-1 py-1 px-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-lg transition-colors shadow-xs"
                    >
                      <EnvelopeIcon className="w-3.5 h-3.5" />
                      Email Tenant
                    </button>
                  )}
                  <a
                    href={`/renewal-inbox/${e.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm"
                  >
                    <EnvelopeIcon className="w-3.5 h-3.5" />
                    Open Inbox
                  </a>
                </div>
              </div>,
              document.body,
            )}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-600 whitespace-nowrap">
          {e.rent ? (
            `$${Number(e.rent).toLocaleString()}`
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-xs whitespace-nowrap">
          {item.attentionType === "no_lease_date" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold border border-gray-200">
              No date set
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[10px] font-semibold border border-red-100">
              {e.leaseTo}
            </span>
          )}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-400 text-right whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          View Details →
        </td>
      </tr>
    );
  };

  const requiresAttentionCount = noLeaseItems.length + expiredItems.length;

  return (
    <div className="pb-6 mt-4">
      {/* 1. Requires Attention Panel */}
      {requiresAttentionCount > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm overflow-hidden flex-shrink-0">
          {/* Panel Header */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200">
            <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-xs font-extrabold text-amber-800 uppercase tracking-wider">
              Requires Attention (Missing or Expired Lease End Date)
            </span>
            <span className="ml-auto text-[10px] font-bold text-amber-600 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
              {requiresAttentionCount} unit{requiresAttentionCount !== 1 ? "s" : ""}
            </span>
          </div>

          {/* No Lease Date Section */}
          {noLeaseItems.length > 0 && (
            <div className="border-b border-amber-100 last:border-0">
              <div className="w-full flex items-center gap-2 px-4 py-2 text-left bg-slate-50">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-700">
                  No Lease End Date ({noLeaseItems.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50/80 border-y border-slate-100">
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Unit
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Building
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        Tenant
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Rent
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Lease End
                      </th>
                      <th className="px-3 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {noLeaseItems.map((item) => renderRow(item))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Expired Lease Section */}
          {expiredItems.length > 0 && (
            <div className="border-b border-amber-100 last:border-0">
              <div className="w-full flex items-center gap-2 px-4 py-2 text-left bg-slate-50">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-red-700">
                  Expired Lease ({expiredItems.length})
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50/80 border-y border-slate-100">
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Unit
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Building
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        Tenant
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Rent
                      </th>
                      <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                        Lease End
                      </th>
                      <th className="px-3 py-1.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {expiredItems.map((item) => renderRow(item))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface MarketingPanelProps {
  items: RenewalAttentionApiEntry[];
  onEntryClick: (e: RentalRollEntry) => void;
  onComposeEmail: (to: string, name: string, subject: string, body: string, entryId: string) => void;
}

const MarketingPanel: React.FC<MarketingPanelProps> = ({
  items,
  onEntryClick,
  onComposeEmail,
}) => {
  const [activePopupId, setActivePopupId] = React.useState<string | null>(null);
  const [popupDirection, setPopupDirection] = React.useState<"above" | "below">(
    "below",
  );
  const [popupCoords, setPopupCoords] = React.useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    entryId: string,
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const showAbove = spaceBelow < 250 && spaceAbove > spaceBelow;

    setActivePopupId(entryId);
    setPopupDirection(showAbove ? "above" : "below");
    setPopupCoords({
      top: showAbove ? rect.top + scrollY : rect.bottom + scrollY,
      left: rect.left + scrollX,
    });
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActivePopupId(null);
    }, 250);
  };

  const handlePopupMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const notRenewingItems = items.filter((i) => i.attentionType === "not_renewing");
  const beingMarketedItems = items.filter((i) => i.attentionType === "being_marketed");

  if (notRenewingItems.length === 0 && beingMarketedItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white border border-gray-200 rounded-xl shadow-xs text-center mt-4">
        <CheckCircleIcon className="w-12 h-12 text-green-500 mb-2" />
        <h3 className="text-sm font-bold text-gray-800">No Marketing Action Needed</h3>
        <p className="text-xs text-gray-400 mt-1 max-w-sm">
          No units are currently marked as Not Renewing or Being Marketed.
        </p>
      </div>
    );
  }

  const renderRow = (item: RenewalAttentionApiEntry) => {
    const e = mapApiRenewal(item.entry);
    return (
      <tr
        key={e.id}
        className="hover:bg-slate-50 transition-colors cursor-pointer group"
        onClick={() => onEntryClick(e)}
      >
        <td className="px-3 py-1.5 text-xs font-semibold text-gray-800 whitespace-nowrap">
          {e.unit}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-600 whitespace-nowrap">
          {e.building}
        </td>
        <td
          className={`px-3 py-1.5 relative transition-colors align-middle ${activePopupId === e.id ? "z-50" : "z-20"} whitespace-nowrap text-xs`}
        >
          <div
            onMouseEnter={
              e.tenant ? (event) => handleMouseEnter(event, e.id) : undefined
            }
            onMouseLeave={e.tenant ? handleMouseLeave : undefined}
            className={
              e.tenant ? "cursor-pointer inline-flex items-center gap-1" : ""
            }
          >
            <div
              className={`font-semibold text-gray-900 ${e.tenant ? "hover:text-blue-600" : ""} transition-colors flex items-center gap-1 justify-start`}
            >
              <span
                className={`transition-colors ${activePopupId === e.id ? "text-blue-600" : e.tenant ? "text-gray-900" : "text-gray-400 font-normal italic"}`}
              >
                {e.tenant ? e.tenant.split(",")[0].trim() : "—"}
              </span>
              {e.tenant && (
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 transition-colors flex-shrink-0 ${activePopupId === e.id ? "text-blue-600" : "text-gray-400"}`}
                />
              )}
            </div>
          </div>
          {activePopupId === e.id &&
            e.tenant &&
            createPortal(
              <div
                onMouseEnter={handlePopupMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  position: "absolute",
                  top: `${popupCoords.top}px`,
                  left: `${popupCoords.left}px`,
                  transform: popupDirection === "above" ? "translateY(-100%) translateY(-4px)" : "translateY(4px)",
                }}
                className={`z-[9999] w-80 bg-white opacity-100 shadow-2xl border border-slate-200 p-4 text-left space-y-3.5 transition-all duration-200 scale-100 ${
                  popupDirection === "above" ? "rounded-t-xl rounded-r-xl" : "rounded-b-xl rounded-r-xl"
                }`}
              >
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                  <div className="flex items-center gap-2">
                    <UserGroupIcon className="w-5 h-5 text-blue-500" />
                    <div>
                      <h4 className="text-xs font-bold text-slate-800">
                        Occupants List
                      </h4>
                      <p className="text-[10px] text-slate-400">
                        {e.occupants?.length || 0} person(s) registered
                      </p>
                    </div>
                  </div>
                  <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full border border-blue-100 bg-blue-50 text-blue-700 uppercase tracking-wider">
                    {e.unit}
                  </span>
                </div>

                <div className="space-y-2 max-h-[195px] overflow-y-auto pr-1 scrollbar-thin">
                  {e.occupants && e.occupants.length > 0 ? (
                    e.occupants.map((o) => (
                      <div
                        key={o.id}
                        className="flex flex-col p-2 bg-slate-50 rounded-lg hover:bg-slate-100/80 transition-colors border border-slate-100"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-slate-800">
                            {o.name}
                          </span>
                          {o.isCosigner && (
                            <span className="text-[8px] font-bold px-1 py-0.2 rounded bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider">
                              Cosigner
                            </span>
                          )}
                        </div>
                        {o.email && (
                          <span className="text-[10px] text-slate-500 mt-0.5 break-all">
                            {o.email}
                          </span>
                        )}
                        {o.phone && (
                          <span className="text-[10px] text-slate-400 mt-0.5">
                            {o.phone}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-xs text-slate-400 italic">
                      No occupants listed
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex gap-2">
                  {e.email && (
                    <button
                      onClick={() =>
                        onComposeEmail(
                          e.email,
                          e.tenant,
                          `Lease Renewal Request - Unit ${e.unit}`,
                          `Dear ${e.tenant},\n\nThis is a friendly reminder regarding your lease renewal for Unit ${e.unit}.\n\nBest regards,\nManagement`,
                          e.id
                        )
                      }
                      className="flex-1 inline-flex items-center justify-center gap-1 py-1 px-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-lg transition-colors shadow-xs"
                    >
                      <EnvelopeIcon className="w-3.5 h-3.5" />
                      Email Tenant
                    </button>
                  )}
                  <a
                    href={`/renewal-inbox/${e.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm"
                  >
                    <EnvelopeIcon className="w-3.5 h-3.5" />
                    Open Inbox
                  </a>
                </div>
              </div>,
              document.body,
            )}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-600 whitespace-nowrap">
          {e.rent ? (
            `$${Number(e.rent).toLocaleString()}`
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-xs whitespace-nowrap">
          {item.attentionType === "not_renewing" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200">
              Not Renewing ({e.leaseTo || "—"})
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-semibold border border-purple-200">
              Being Marketed ({e.leaseTo || "—"})
            </span>
          )}
        </td>
        <td className="px-3 py-1.5 text-xs text-gray-400 text-right whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          View Details →
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6 pb-6 mt-4">
      {/* 1. Not Renewing Panel */}
      {notRenewingItems.length > 0 && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 shadow-sm overflow-hidden flex-shrink-0">
          {/* Panel Header */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border-b border-blue-200">
            <PaperAirplaneIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-xs font-extrabold text-blue-800 uppercase tracking-wider">
              Not Renewing Units
            </span>
            <span className="ml-auto text-[10px] font-bold text-blue-600 bg-blue-100 border border-blue-200 px-2 py-0.5 rounded-full">
              {notRenewingItems.length} unit{notRenewingItems.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/80 border-y border-slate-100">
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Unit
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Building
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Rent
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Lease End
                  </th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {notRenewingItems.map((item) => renderRow(item))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. Being Marketed Panel */}
      {beingMarketedItems.length > 0 && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 shadow-sm overflow-hidden flex-shrink-0">
          {/* Panel Header */}
          <div className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 border-b border-purple-200">
            <ArrowTrendingUpIcon className="w-4 h-4 text-purple-500 flex-shrink-0" />
            <span className="text-xs font-extrabold text-purple-800 uppercase tracking-wider">
              Being Marketed Units
            </span>
            <span className="ml-auto text-[10px] font-bold text-purple-600 bg-purple-100 border border-purple-200 px-2 py-0.5 rounded-full">
              {beingMarketedItems.length} unit{beingMarketedItems.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50/80 border-y border-slate-100">
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Unit
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Building
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Rent
                  </th>
                  <th className="px-3 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    Lease End
                  </th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {beingMarketedItems.map((item) => renderRow(item))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const RenewalDashboard: React.FC = () => {
  const [zoom, setZoom] = useState<ZoomLevel>(0);
  const [currentYear, setCurrentYear] = useState(TODAY.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(TODAY.getMonth());
  const [currentQuarter, setCurrentQuarter] = useState(
    Math.floor(TODAY.getMonth() / 3),
  );
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeStatuses, setActiveStatuses] = useState<
    Record<RenewalStage, boolean>
  >({
    up_for_renewal: true,
    sent_out: true,
    renewed: true,
    partially_signed: true,
    not_renewing: true,
    being_marketed: true,
    rented: true,
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Comparable filtering states
  const [lineFilter, setLineFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [buildingFilter, setBuildingFilter] = useState<string>("all");
  const [apartmentFilter, setApartmentFilter] = useState<string>("all");
  const [unitFilter, setUnitFilter] = useState<string>("all");

  const [selectedEntry, setSelectedEntry] = useState<RentalRollEntry | null>(
    null,
  );
  const [data, setData] = useState<RentalRollEntry[]>([]);
  const [attentionData, setAttentionData] = useState<
    RenewalAttentionApiEntry[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [customCreatedLines, setCustomCreatedLines] = useState<
    Record<string, string[]>
  >({});
  const [customCreatedGroups, setCustomCreatedGroups] = useState<
    Record<string, string[]>
  >({});

  const handleAddCustomLine = useCallback((building: string, line: string) => {
    setCustomCreatedLines((prev) => {
      const existing = prev[building] || [];
      if (existing.includes(line)) return prev;
      return { ...prev, [building]: [...existing, line] };
    });
  }, []);

  const handleAddCustomGroup = useCallback(
    (building: string, group: string) => {
      setCustomCreatedGroups((prev) => {
        const existing = prev[building] || [];
        if (existing.includes(group)) return prev;
        return { ...prev, [building]: [...existing, group] };
      });
    },
    [],
  );

  const [emailCompose, setEmailCompose] = useState<{
    renewalId?: string;
    recipients: string;
    names: string;
    subject: string;
    body: string;
  } | null>(null);

  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleComposeEmail = useCallback(
    (
      recipients: string,
      names: string,
      subject: string,
      body: string,
      renewalId?: string,
    ) => {
      setEmailCompose({ renewalId, recipients, names, subject, body });
    },
    [],
  );

  const loadRenewalData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [rows, attentionItems] = await Promise.all([
        fetchRenewals(),
        fetchAttentionUnits(),
      ]);
      const mappedRows = rows.map(mapApiRenewal);
      setData(mappedRows);
      setAttentionData(attentionItems);
      setSelectedEntry((current) =>
        current
          ? mappedRows.find((row) => row.id === current.id) || null
          : null,
      );
    } catch (error) {
      console.error("Failed to load renewal records", error);
      setLoadError("Unable to load renewal records.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRenewalData();
  }, [loadRenewalData]);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollCooldown = useRef(false);

  // Ctrl+scroll to zoom (only active when Calendar view is visible)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (scrollCooldown.current) return;
      scrollCooldown.current = true;
      setTimeout(() => {
        scrollCooldown.current = false;
      }, 500);
      if (e.deltaY > 0) {
        setZoom((z) => {
          if (z >= 3) return z;
          return Math.min(2, z + 1) as ZoomLevel;
        });
      } else {
        setZoom((z) => {
          if (z >= 3) return z;
          return Math.max(0, z - 1) as ZoomLevel;
        });
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    setCurrentQuarter(Math.floor(currentMonth / 3));
  }, [currentMonth]);

  const handleStageChange = useCallback(
    async (
      id: string,
      stage: RenewalStage,
      notes: string,
      newRent: number,
      unitLine: string,
      compGroup: string,
      currentRent?: number,
    ) => {
      const today = toIsoDate(TODAY);
      setData((prev) =>
        prev.map((e) =>
          e.id === id
            ? {
                ...e,
                stage,
                notes,
                newRent,
                rent: currentRent !== undefined ? currentRent : e.rent,
                unitLine,
                comparableGroup: compGroup,
                marketingStartDate:
                  stage === "being_marketed" && !e.marketingStartDate
                    ? today
                    : e.marketingStartDate,
                showingStats: stage === "rented" ? undefined : e.showingStats,
              }
            : e,
        ),
      );

      try {
        const saved = await updateRenewal(id, {
          dashboardStatus: stageToApiStatus(stage),
          newRent,
          currentRent,
          notes,
          renewalLine: unitLine,
          comparableGroup: compGroup,
        });
        const mapped = mapApiRenewal(saved);
        setData((prev) => prev.map((e) => (e.id === id ? mapped : e)));
        setSelectedEntry((current) => (current?.id === id ? mapped : current));
        setToast("Renewal changes saved.");
        
        // Refresh attention units dynamically so tabs update immediately
        fetchAttentionUnits().then(setAttentionData).catch(console.error);
      } catch (error) {
        console.error("Failed to save renewal changes", error);
        setToast("Failed to save renewal changes.");
        await loadRenewalData();
      }
    },
    [loadRenewalData],
  );

  const handleMoveToMarketing = useCallback(
    (id: string) => {
      const entry = data.find((e) => e.id === id);
      if (!entry) return;
      void handleStageChange(
        id,
        "being_marketed",
        entry.notes,
        entry.newRent,
        entry.unitLine || "",
        entry.comparableGroup || "",
      );
    },
    [data, handleStageChange],
  );

  const handleMoveToRented = useCallback(
    (id: string) => {
      const entry = data.find((e) => e.id === id);
      if (!entry) return;
      void handleStageChange(
        id,
        "rented",
        entry.notes,
        entry.newRent,
        entry.unitLine || "",
        entry.comparableGroup || "",
      );
    },
    [data, handleStageChange],
  );

  const filteredData = useMemo(
    () =>
      data.filter((e) => {
        const matchSearch =
          !search ||
          e.tenant.toLowerCase().includes(search.toLowerCase()) ||
          e.unit.toLowerCase().includes(search.toLowerCase()) ||
          e.building.toLowerCase().includes(search.toLowerCase());

        const matchStatus = activeStatuses[e.stage];

        // Comparable filters
        const matchLine =
          lineFilter === "all" ||
          e.unitLine?.toLowerCase() === lineFilter.toLowerCase();
        const matchGroup =
          groupFilter === "all" ||
          e.comparableGroup?.toLowerCase() === groupFilter.toLowerCase();
        const matchBuilding =
          buildingFilter === "all" ||
          e.building.toLowerCase() === buildingFilter.toLowerCase();
        const matchApartment =
          apartmentFilter === "all" ||
          e.buildingAddress.toLowerCase() === apartmentFilter.toLowerCase();
        const matchUnit =
          unitFilter === "all" ||
          e.unit.toLowerCase() === unitFilter.toLowerCase();

        return (
          matchSearch &&
          matchStatus &&
          matchLine &&
          matchGroup &&
          matchBuilding &&
          matchApartment &&
          matchUnit
        );
      }),
    [
      data,
      search,
      activeStatuses,
      lineFilter,
      groupFilter,
      buildingFilter,
      apartmentFilter,
      unitFilter,
    ],
  );

  // Extract all lines and groups dynamically for filter selectors
  const allLines = useMemo(() => {
    const lines = new Set<string>();
    data.forEach((e) => {
      if (e.unitLine) lines.add(e.unitLine);
    });
    Object.values(customCreatedLines).forEach((list) => {
      list.forEach((line) => lines.add(line));
    });
    return Array.from(lines).sort();
  }, [data, customCreatedLines]);

  const allGroups = useMemo(() => {
    const groups = new Set<string>();
    data.forEach((e) => {
      if (buildingFilter !== "all" && e.building !== buildingFilter) return;
      if (apartmentFilter !== "all" && e.buildingAddress !== apartmentFilter)
        return;
      if (e.comparableGroup) groups.add(e.comparableGroup);
    });
    if (buildingFilter !== "all") {
      const list = customCreatedGroups[buildingFilter] || [];
      list.forEach((group) => groups.add(group));
    } else {
      Object.values(customCreatedGroups).forEach((list) => {
        list.forEach((group) => groups.add(group));
      });
    }
    return Array.from(groups).sort();
  }, [data, customCreatedGroups, buildingFilter, apartmentFilter]);

  const allBuildings = useMemo(() => {
    const buildings = new Set<string>();
    data.forEach((e) => {
      if (e.building) buildings.add(e.building);
    });
    return Array.from(buildings).sort();
  }, [data]);

  const allApartments = useMemo(() => {
    const apartments = new Set<string>();
    data.forEach((e) => {
      if (buildingFilter !== "all" && e.building !== buildingFilter) return;
      if (e.buildingAddress) apartments.add(e.buildingAddress);
    });
    return Array.from(apartments).sort();
  }, [data, buildingFilter]);

  const allUnits = useMemo(() => {
    const units = new Set<string>();
    data.forEach((e) => {
      if (buildingFilter !== "all" && e.building !== buildingFilter) return;
      if (apartmentFilter !== "all" && e.buildingAddress !== apartmentFilter)
        return;
      if (e.unit) units.add(e.unit);
    });
    return Array.from(units).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [data, buildingFilter, apartmentFilter]);

  const navPrev = () => {
    if (zoom === 2) {
      const m = currentMonth - 3;
      if (m < 0) {
        setCurrentYear((y) => y - 1);
        setCurrentMonth(m + 12);
      } else setCurrentMonth(m);
    } else {
      if (currentMonth === 0) {
        setCurrentYear((y) => y - 1);
        setCurrentMonth(11);
      } else setCurrentMonth((m) => m - 1);
    }
  };
  const navNext = () => {
    if (zoom === 2) {
      const m = currentMonth + 3;
      if (m > 11) {
        setCurrentYear((y) => y + 1);
        setCurrentMonth(m - 12);
      } else setCurrentMonth(m);
    } else {
      if (currentMonth === 11) {
        setCurrentYear((y) => y + 1);
        setCurrentMonth(0);
      } else setCurrentMonth((m) => m + 1);
    }
  };

  const currentLabel =
    zoom === 2
      ? `Q${currentQuarter + 1} ${currentYear} — ${SHORT_MONTHS[currentQuarter * 3]}–${SHORT_MONTHS[currentQuarter * 3 + 2]}`
      : `${MONTHS[currentMonth]} ${currentYear}`;

  return (
    <div
      ref={containerRef}
      className="h-[calc(100vh-76px)] bg-gray-50 flex flex-col overflow-hidden"
    >
      {/* Top Header Section */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CalendarDaysIcon className="w-7 h-7 text-gray-500" />
              Renewal Dashboard
            </h1>
          </div>
        </div>

        {/* View Switches & Navigation */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Zoom Switcher */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {([0, 1, 2, 3, 4] as ZoomLevel[]).map((z) => (
              <button
                key={z}
                onClick={() => setZoom(z)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  zoom === z
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {z === 0 && <CalendarDaysIcon className="w-3.5 h-3.5" />}
                {z === 1 && <ListBulletIcon className="w-3.5 h-3.5" />}
                {z === 2 && <span className="font-bold text-[10px]">3M</span>}
                {z === 3 && (
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500" />
                )}
                {z === 4 && (
                  <ArrowTrendingUpIcon className="w-3.5 h-3.5 text-purple-500" />
                )}
                <span>{ZOOM_LABELS[z]}</span>
              </button>
            ))}
          </div>

          {/* Calendar Nav Arrows */}
          {zoom !== 3 && zoom !== 4 && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={navPrev}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-sm font-semibold text-gray-800 min-w-[130px] text-center">
                {currentLabel}
              </span>
              <button
                onClick={navNext}
                className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRightIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body Flex Row Container */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Sidebar Filter Panel — collapsible with arrow */}
        <div
          className={`relative border-r border-gray-200 bg-white flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out ${sidebarOpen ? "w-64" : "w-0"}`}
        >
          {/* Arrow toggle on the right edge */}
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            className="absolute -right-3.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center hover:bg-blue-50 hover:border-blue-300 transition-colors focus:outline-none"
            title={sidebarOpen ? "Collapse Filters" : "Expand Filters"}
          >
            {sidebarOpen ? (
              <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>

          {sidebarOpen && (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-150 bg-slate-50/50 flex items-center justify-between">
                <h2 className="text-xs font-bold text-slate-850 uppercase tracking-wider flex items-center gap-2">
                  <FunnelIcon className="w-4 h-4 text-blue-600" />
                  Dashboard Filters
                </h2>
                <button
                  onClick={() => {
                    setSearch("");
                    setActiveStatuses({
                      up_for_renewal: true,
                      sent_out: true,
                      renewed: true,
                      partially_signed: true,
                      not_renewing: true,
                      being_marketed: true,
                      rented: true,
                    });
                    setLineFilter("all");
                    setGroupFilter("all");
                    setBuildingFilter("all");
                    setApartmentFilter("all");
                    setUnitFilter("all");
                    setStatusDropdownOpen(false);
                  }}
                  className="text-xs text-blue-650 hover:text-blue-800 font-bold hover:underline transition-all focus:outline-none"
                >
                  Reset All
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="p-4 space-y-4 flex-1 overflow-y-auto scrollbar-thin bg-white">
                {/* Search Input */}
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    Search
                  </label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search tenant, unit, building..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 border border-slate-205 rounded-lg text-xs font-medium text-slate-800 bg-white placeholder-slate-400 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 transition-all shadow-2xs"
                    />
                  </div>
                </div>

                <div className="h-px bg-slate-100 my-1" />

                {/* Status Filter Checklist (collapsible dropdown) */}
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen((o) => !o)}
                    className="w-full flex items-center justify-between py-1 border-b border-slate-100 group text-left"
                  >
                    <label className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider cursor-pointer group-hover:text-slate-700 transition-colors">
                      Status Checklist
                    </label>
                    <ChevronDownIcon
                      className={`w-3.5 h-3.5 text-slate-450 transition-transform duration-200 group-hover:text-slate-655 ${statusDropdownOpen ? "rotate-180" : ""}`}
                    />
                  </button>

                  {statusDropdownOpen && (
                    <div className="pt-0.5 space-y-2">
                      <div className="flex justify-end gap-2 text-[9px] font-bold text-blue-600 pb-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            const allOn = {} as Record<RenewalStage, boolean>;
                            STAGE_ORDER.forEach((s) => (allOn[s] = true));
                            setActiveStatuses(allOn);
                          }}
                          className="hover:underline hover:text-blue-800"
                        >
                          All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={() => {
                            const allOff = {} as Record<RenewalStage, boolean>;
                            STAGE_ORDER.forEach((s) => (allOff[s] = false));
                            setActiveStatuses(allOff);
                          }}
                          className="hover:underline hover:text-blue-800"
                        >
                          None
                        </button>
                      </div>
                      <div className="space-y-1 bg-slate-50/50 border border-slate-100 p-2 rounded-xl shadow-3xs">
                        {STAGE_ORDER.map((s) => {
                          const c = STAGE_CFG[s];
                          const active = activeStatuses[s];
                          return (
                            <button
                              key={s}
                              type="button"
                              onClick={() =>
                                setActiveStatuses((prev) => ({
                                  ...prev,
                                  [s]: !prev[s],
                                }))
                              }
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs transition-all border ${
                                active
                                  ? `${c.bg} ${c.text} ${c.border} font-semibold shadow-2xs`
                                  : "bg-white border-transparent text-slate-600 hover:bg-slate-100/60 hover:border-slate-200"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={active}
                                readOnly
                                className="rounded text-blue-600 border-slate-350 focus:ring-blue-500/30 w-3 h-3 pointer-events-none flex-shrink-0"
                              />
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${active ? c.dot : "bg-gray-300"} flex-shrink-0`}
                              />
                              <span className="truncate flex-1 text-[11px]">
                                {c.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="h-px bg-slate-100 my-1" />

                {/* Property Details Header Label */}
                <div className="pt-1">
                  <span className="block text-[10px] font-bold text-slate-450 uppercase tracking-wider">
                    Property Filters
                  </span>
                </div>

                {/* Building Filter */}
                <div className="space-y-1">
                  <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider pl-0.5">
                    Building
                  </label>
                  <FilterDropdown
                    value={buildingFilter}
                    onChange={setBuildingFilter}
                    options={allBuildings}
                    placeholder="All Buildings"
                  />
                </div>

                {/* Apartment Filter */}
                <div className="space-y-1">
                  <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider pl-0.5">
                    Apartment
                  </label>
                  <FilterDropdown
                    value={apartmentFilter}
                    onChange={setApartmentFilter}
                    options={allApartments}
                    placeholder="All Apartments"
                    displayMapping={(a) => a.split(" New York")[0] || a}
                  />
                </div>

                {/* Unit Filter */}
                <div className="space-y-1">
                  <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider pl-0.5">
                    Unit
                  </label>
                  <FilterDropdown
                    value={unitFilter}
                    onChange={setUnitFilter}
                    options={allUnits}
                    placeholder="All Units"
                    displayMapping={(u) => `Unit ${u}`}
                  />
                </div>

                {/* Line Filter */}
                <div className="space-y-1">
                  <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider pl-0.5">
                    Line
                  </label>
                  <FilterDropdown
                    value={lineFilter}
                    onChange={setLineFilter}
                    options={allLines}
                    placeholder="All Lines"
                  />
                </div>

                {/* Comparable Group */}
                <div className="space-y-1">
                  <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider pl-0.5">
                    Comparable Group
                  </label>
                  <FilterDropdown
                    value={groupFilter}
                    onChange={setGroupFilter}
                    options={allGroups}
                    placeholder="All Groups"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden pt-3 px-3 pb-3 space-y-4 flex flex-col">
          {/* Comparable Filter Stats Alert (if active) */}
          {(lineFilter !== "all" || groupFilter !== "all") && (
            <div className="flex items-center justify-between bg-blue-50/80 border border-blue-200 px-4 py-3 rounded-lg text-xs text-blue-800 font-medium shadow-sm">
              <div className="flex items-center gap-4">
                <span>
                  Units Found:{" "}
                  <strong className="font-bold">{filteredData.length}</strong>
                </span>
                <span>
                  Avg Current Rent:{" "}
                  <strong className="font-bold">
                    $
                    {filteredData.length
                      ? Math.round(
                          filteredData.reduce(
                            (acc, curr) => acc + curr.rent,
                            0,
                          ) / filteredData.length,
                        ).toLocaleString()
                      : 0}
                    /mo
                  </strong>
                </span>
                <span>
                  Avg Proposed Rent:{" "}
                  <strong className="font-bold">
                    $
                    {filteredData.length
                      ? Math.round(
                          filteredData.reduce(
                            (acc, curr) => acc + curr.newRent,
                            0,
                          ) / filteredData.length,
                        ).toLocaleString()
                      : 0}
                    /mo
                  </strong>
                </span>
              </div>
              <button
                onClick={() => {
                  setLineFilter("all");
                  setGroupFilter("all");
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold focus:outline-none"
              >
                Clear Stats
              </button>
            </div>
          )}

          {/* Calendar / Month List / Quarter View Content */}
          {loading ? (
            <div className="bg-white rounded-lg border border-gray-200 p-16 text-center shadow-sm">
              <ArrowPathIcon className="w-10 h-10 text-blue-500 mx-auto mb-3 animate-spin" />
              <p className="text-gray-600 font-semibold">
                Loading renewal records...
              </p>
            </div>
          ) : loadError ? (
            <div className="bg-white rounded-lg border border-red-200 p-16 text-center shadow-sm">
              <p className="text-red-700 font-semibold">{loadError}</p>
              <button
                type="button"
                onClick={loadRenewalData}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-6 flex-1 flex flex-col min-h-0">
              {zoom === 0 && (
                <CalendarView
                  data={filteredData}
                  year={currentYear}
                  month={currentMonth}
                  onEntryClick={setSelectedEntry}
                />
              )}
              {zoom === 1 && (
                <MonthListView
                  data={filteredData}
                  year={currentYear}
                  month={currentMonth}
                  onEntryClick={setSelectedEntry}
                  onMoveToMarketing={handleMoveToMarketing}
                  onMoveToRented={handleMoveToRented}
                  onComposeEmail={handleComposeEmail}
                />
              )}
              {zoom === 2 && (
                <QuarterView
                  data={filteredData}
                  year={currentYear}
                  quarter={currentQuarter}
                  onEntryClick={setSelectedEntry}
                />
              )}
              {zoom === 3 && (
                <div className="flex-1 overflow-y-auto min-h-0">
                  <AttentionPanel
                    items={attentionData}
                    onEntryClick={setSelectedEntry}
                    onComposeEmail={handleComposeEmail}
                  />
                </div>
              )}
              {zoom === 4 && (
                <div className="flex-1 overflow-y-auto min-h-0">
                  <MarketingPanel
                    items={attentionData}
                    onEntryClick={setSelectedEntry}
                    onComposeEmail={handleComposeEmail}
                  />
                </div>
              )}
            </div>
          )}

          {/* Bottom Legend */}
          {zoom !== 1 && (
            <div className="mt-auto bg-white rounded-lg border border-gray-200 p-4 flex flex-wrap gap-4 text-xs text-gray-500 items-center justify-between shadow-sm">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-semibold text-gray-700">
                  Status Colors:
                </span>
                {STAGE_ORDER.map((s) => {
                  const c = STAGE_CFG[s];
                  return (
                    <span
                      key={s}
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border ${c.bg} ${c.text} ${c.border} font-medium`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      {c.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Entry Detail Modal */}
      {selectedEntry && (
        <EntryModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onStageChange={handleStageChange}
          allEntries={data}
          onComposeEmail={handleComposeEmail}
          customCreatedLines={customCreatedLines}
          customCreatedGroups={customCreatedGroups}
          onAddCustomLine={handleAddCustomLine}
          onAddCustomGroup={handleAddCustomGroup}
        />
      )}

      {/* Toast Notification Banner */}
      {toast && (
        <div className="fixed top-5 right-5 z-[60] bg-slate-900 text-white border border-slate-700/80 px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 animate-slide-in text-xs font-semibold">
          <CheckCircleIcon className="w-4 h-4 text-green-400" />
          <span>{toast}</span>
        </div>
      )}

      {/* Compose Email Modal Overlay */}
      {emailCompose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white border border-slate-200 shadow-2xl rounded-2xl w-full max-w-lg flex flex-col overflow-hidden transform scale-100 transition-transform duration-200">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeIcon className="w-5 h-5 text-blue-600 animate-pulse" />
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
                  Compose Email
                </h3>
              </div>
              <button
                onClick={() => setEmailCompose(null)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body / Inputs */}
            <div className="p-6 space-y-4 flex-1">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  To:
                </label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 select-all truncate">
                  {emailCompose.names} &lt;{emailCompose.recipients}&gt;
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailCompose.subject}
                  onChange={(e) =>
                    setEmailCompose({
                      ...emailCompose,
                      subject: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                  Message Body
                </label>
                <textarea
                  rows={8}
                  value={emailCompose.body}
                  onChange={(e) =>
                    setEmailCompose({ ...emailCompose, body: e.target.value })
                  }
                  placeholder="Type your message here..."
                  className="w-full px-3.5 py-3 border border-slate-200 rounded-xl text-xs font-medium text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none shadow-sm"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
              <button
                onClick={() => setEmailCompose(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!emailCompose.renewalId) {
                    setToast("Missing renewal record for email.");
                    return;
                  }

                  const recipients = emailCompose.recipients
                    .split(/[;,]/)
                    .map((recipient) => recipient.trim())
                    .filter(Boolean);

                  if (recipients.length === 0) {
                    setToast("Add at least one recipient.");
                    return;
                  }

                  setSendingEmail(true);
                  try {
                    await sendRenewalEmail(emailCompose.renewalId, {
                      recipients,
                      subject: emailCompose.subject,
                      body: emailCompose.body,
                    });
                    setToast(
                      `Email successfully sent to ${emailCompose.names}!`,
                    );
                    setEmailCompose(null);
                    await loadRenewalData();
                  } catch (error) {
                    console.error("Failed to send renewal email", error);
                    setToast("Failed to send renewal email.");
                  } finally {
                    setSendingEmail(false);
                  }
                }}
                disabled={sendingEmail}
                className="px-5 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-md transition-colors"
              >
                <PaperAirplaneIcon className="w-3.5 h-3.5" />
                {sendingEmail ? "Sending..." : "Send Mail"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenewalDashboard;
